// API service functions for the Enterprise Portal

import { useAuthStore } from '@/stores/auth'
import { hc } from 'hono/client'
import { apiRequest } from './utils/requests'
import { DEFAULT_SITE_ID } from './utils/constants'
import { apiUtils } from './utils/api-utils'
import { queryKeys } from './query-keys'
import { uploadAPI } from './services/upload'

export { apiUtils } from './utils/api-utils'
export { queryKeys } from './query-keys'

import type { Visitor as SessionVisitor } from '@/server/routes/sessions/hono-sessions'
import type { SessionTakeoverOptions } from './services/sessions'
import type {
  FlowNodeModuleDefinition,
  FlowNodeModuleType,
} from '@shared/flows/modules'
import type {
  // Admin Types
  AdminUser,
  AdminUserCreationRequest,
  AdminUserResponse,
  AdminUserUpdateRequest,
  Announcement,
  ApiResponse,
  AdminSite,
  Bot,
  BotCreationRequest,
  BotUpdateRequest,
  Collection,
  CollectionCreateRequest,
  CollectionUpdateRequest,
  AgentAssignedSessionsPage,
  HandoverRequest,
  NewsItem,
  PaginatedResponse,
  S3ConfigurationCreationRequest,
  S3ConfigurationPreset,
  S3ConfigurationResponse,
  S3ConfigurationUpdateRequest,
  S3CorsRule,
  S3TestResult,
  ErpnextConfig,
  ErpnextConfigPayload,
  SearchParams,
  SearchResult,
  ServiceConfiguration,
  ServiceCreationRequest,
  ServiceUpdateRequest,
  SiteUpdateRequest,
  Team,
  UserMobile,
  UserMobileCreateRequest,
  UserMobileUpdateRequest,
  LlmProviderPricing,
  LlmPricingRequest,
  LlmUsageListResponse,
  LlmUsageRow,
  PasswordResetResponsePayload,
  PasswordResetSubmitPayload,
  ProfileOtpOptions,
  ProfileOtpRequestPayload,
  ProfileOtpRequestResponse,
  ProfileOtpVerifyPayload,
  ProfileOtpVerifyResponse,
  FacebookConversationContact,
  MessageTemplate,
  MessageTemplatePayload,
  MessageTemplateUpdatePayload,
  MessagingChannel,
  ERPNextProduct,
  ERPNextProductListParams,
  ERPNextProductListResponse,
  ERPNextProductPayload,
  ERPNextAttribute,
  ERPNextTemplatePayload,
  ERPNextVariantPayload,
  ERPNextGenerateVariantsPayload,
  ERPNextCustomer,
  ERPNextCustomerListParams,
  ERPNextCustomerListResponse,
  ERPNextCustomerPayload,
  ERPNextAddress,
  ERPNextAddressPayload,
  ERPNextAddressListResponse,
  ERPNextOrder,
  ERPNextOrderPayload,
  ERPNextOrderListResponse,
  ERPNextOrderListParams,
  ERPNextOrderItem,
  ERPNextWarehouse,
  ERPNextWarehouseListResponse,
  ElevenLabsConfig,
  ElevenLabsConfigPayload,
  ElevenLabsConfigUpdatePayload,
  ElevenLabsPushRequest,
  ElevenLabsPushResponse,
  EmbedConfiguration,
  EmbedConfigPayload,
  EmbedConfigUpdatePayload,
  VoiceRealtimeConfig,
  VoiceRealtimeConfigPayload,
  VoiceRealtimeConfigUpdatePayload,
  VoiceRealtimeConfigSummary,
  VoiceRealtimeSessionRequest,
  VoiceRealtimeSessionResponseData,
  WhatsAppConfigPayload,
  WhatsAppConfig,
  WhatsAppConfigResponse,
  WhatsAppWebhookInfo,
  // Telegram Types
  TelegramConfiguration,
  TelegramTestResponse,
  TelegramWebhookTestResponse,
  Article,
  ArticleQuestion,
  ArticlePayload,
  ArticleUpdatePayload,
  KnowledgeBaseAnswer,
  KnowledgeSkill,
  KnowledgeSkillCollection,
  KnowledgeSkillCollectionInput,
  KnowledgeSkillDocumentInput,
  KnowledgeSkillDocumentProcessResult,
  KnowledgeSkillInput,
  KnowledgeSkillSearchResult,
  RoleDefinition,
  Permission,
  PermissionCreatePayload,
  PermissionUpdatePayload,
  Wallet,
  WalletProvider,
  WalletProviderCreatePayload,
  WalletProviderUpdatePayload,
  WalletCreatePayload,
  WalletUpdatePayload,
  WalletConversionRate,
  WalletConversionRateInput,
  UserWallet,
  WalletJournal,
  WalletJournalListResponse,
  Reward,
  RewardCreatePayload,
  RewardUpdatePayload,
  RewardTracking,
  BonusPromotion,
  BonusPromotionCreatePayload,
  BonusPromotionUpdatePayload,
  BonusJournal,
  BonusJournalCreatePayload,
  BonusJournalUpdatePayload,
  BonusJournalListResponse,
  BonusAgent,
  BonusAgentCreatePayload,
  BonusAgentUpdatePayload,
  BonusAgentJournal,
  BonusAgentJournalListResponse,
  ActiveBonusRecord,
  SettlementPreview,
  SettlementSummary,
  Payout,
  PayoutCreatePayload,
  PayoutUpdatePayload,
  PayoutListParams,
  PayoutListResponse,
  PayoutStatus,
} from '@/types'
import type {
  CrocoBalanceResult,
  CrocoGameInfo,
  CrocoPlaceBetResult,
  CrocoWinningListResponse,
} from '@/types/croco'
import type {
  AiTool,
  AiToolPayload,
  AiToolUpdatePayload,
} from '@/types/ai-tool'
import type { Document, DocumentUploadResponse } from '@/types/document'
import type {
  ConversationCountReport,
  ConversationPerformanceReport,
} from '@/types/reports'
import type {
  ChatFlow,
  Flow,
  FlowAttachment,
  FlowConnection,
  FlowNode,
} from '@/types/flow'
import type { AIProcess, AIProcessExecution } from '@/types/ai-flow'
import type { CrocoTransactionFilters } from '@/types/croco'

type JsonPrimitive = string | number | boolean | null
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue }
type JsonObject = Record<string, JsonValue>

type WhatsAppTemplateComponent = JsonObject

interface KnowledgeSearchItem {
  id: string
  type: string
  score?: number
  title?: string | null
  text?: string | null
  document_id?: string | null
  source_url?: string | null
  wordCount?: number | null
  collection_id?: string | null
  collection_name?: string | null
}

interface KnowledgeSearchResponse {
  items: KnowledgeSearchItem[]
  total: number
  page: number
  limit: number
}

type CollectionListResponse = Collection[] | { items: Collection[] }

const ensureCollectionArray = (
  data: CollectionListResponse | undefined
): Collection[] => {
  if (!data) {
    return []
  }
  return Array.isArray(data) ? data : data.items
}

interface AgentListItem extends AdminUserResponse {
  siteId?: number
  email?: string | null
  firstName?: string | null
  lastName?: string | null
  isActive?: boolean
  isOnline?: boolean
  lastSeenAt?: string | null
  lastLoginAt?: string | null
  teamIds?: number[]
  teamName?: string | null
}

type AgentCreatePayload = AdminUserCreationRequest & {
  role?: AdminUser['role']
}

type AgentUpdatePayload = AdminUserUpdateRequest & {
  role?: AdminUser['role']
}

interface TeamCreatePayload {
  name: string
  description?: string | null
  isActive?: boolean
  missedConversationSlaSeconds?: number | null
}

interface TeamUpdatePayload {
  name?: string
  description?: string | null
  isActive?: boolean
  missedConversationSlaSeconds?: number | null
}

interface UserWalletCreatePayload {
  walletId: number
  userId: number
}

interface WalletJournalCreatePayload {
  walletId: number
  userWalletId: number
  description?: string | null
  credit?: number
  debit?: number
  appRefId?: string | null
  appRefName?: string | null
}

type WalletJournalUpdatePayload = Partial<WalletJournalCreatePayload>

interface TeamReportRecalcPayload {
  start: string
  end: string
  bucket?: 'hour' | 'day'
  agentId?: number | null
  userType?: 'all' | 'registered' | 'guest'
  metadataFilters?: {
    country?: string
    language?: string
    browser?: string
    os?: string
  }
}

interface TeamReportRecalcResponse {
  report: ConversationPerformanceReport
  parameters: {
    teamId: number
    start: string
    end: string
    bucket: 'hour' | 'day'
    agentId: number | null
    userType: 'all' | 'registered' | 'guest'
    metadataFilters: {
      country?: string
      language?: string
      browser?: string
      os?: string
    } | null
  }
}
interface AdminFlowSummary {
  id: number
  siteId: number
  name: string
  slug?: string | null
  description?: string | null
  variables?: JsonObject | null
  isActive: boolean
  isDefault: boolean
  version: number
  createdBy: number
  createdAt: string
  updatedAt: string
}

interface AdminFlowWithDetails extends AdminFlowSummary {
  nodes?: AdminFlowNode[]
  connections?: AdminFlowConnection[]
}

type FlowModuleToggle = FlowNodeModuleDefinition & {
  siteId?: number | null
  enabled: boolean
}

interface AdminFlowNode {
  id: string
  flowId: number
  nodeType: string
  title: string
  positionX: number
  positionY: number
  config: JsonObject
  _v: number
  createdAt: string
  updatedAt: string
}

interface AdminFlowConnection {
  id: string
  flowId: number
  sourceNodeId: string
  targetNodeId: string
  sourceHandle?: string | null
  condition?: JsonObject
  createdAt: string
  updatedAt: string
}

type FlowClonePayload = {
  name?: string
  slug?: string | null
}

interface FlowUpsertPayload {
  name?: string
  slug?: string | null
  description?: string | null
  isActive?: boolean
  isDefault?: boolean
  siteId?: number
}

type FlowNodeUpsertPayload = Partial<AdminFlowNode> & {
  config?: JsonObject
}

type FlowConnectionUpsertPayload = Partial<AdminFlowConnection> & {
  condition?: JsonObject
}

type AIProcessClonePayload = { name?: string }

interface AIProcessTestResponse {
  success: boolean
  message: string
  execution?: AIProcessExecution
}

interface AIProcessPayload {
  name: string
  description?: string | null
  status?: string
  category?: string | null
  tags?: string[]
  config?: JsonObject
  nodes?: JsonObject[]
  edges?: JsonObject[]
  executionCount?: number
  lastRun?: string | null
}

type SessionActionResponse = JsonObject

interface AvailableAgentsResponse {
  agents: AgentListItem[]
}

type ContactRecord = JsonObject
type WhatsAppTemplateRecord = JsonObject
type NotificationRecord = JsonObject
type FlowExecutionResult = JsonObject
type FlowSessionRecord = JsonObject

function resolveAgentContext() {
  const authState =
    typeof window !== 'undefined' ? useAuthStore.getState() : null

  // Prefer an agent token when available, but fall back to the admin token so
  // admin-only views (like the agents dashboard) can reach the sessions API
  // without issuing unauthenticated requests.
  const token = authState?.agentToken ?? authState?.adminToken ?? null

  return {
    token,
  }
}

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const getTrimmedString = (value: JsonValue | undefined): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const toOptionalString = (value: JsonValue | undefined): string | undefined =>
  typeof value === 'string' ? value : undefined

const toOptionalBoolean = (
  value: JsonValue | undefined
): boolean | undefined => (typeof value === 'boolean' ? value : undefined)

const hasDataProperty = (
  payload: JsonObject | null
): payload is JsonObject & { data: unknown } =>
  Boolean(payload && 'data' in payload)

function createSessionsClient() {
  const { token } = resolveAgentContext()
  const baseUrl = `/api/be/sessions`

  const fetchWithAuth: typeof fetch = async (input, init: RequestInit = {}) => {
    const headers = new Headers(init.headers || {})

    if (!(init.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    return fetch(input, {
      ...init,
      headers,
    })
  }

  type SessionsClient = {
    stats: {
      $get: (options?: { query?: { teamId: string } }) => Promise<Response>
    }
    active: {
      $get: (options?: { query?: { teamId: string } }) => Promise<Response>
    }
    assigned: {
      $get: (options?: {
        query?: {
          page?: string
          limit?: string
          includeTeam?: string
          onlyWithVisibleMessages?: string
        }
      }) => Promise<Response>
    }
    list_by_team: {
      $get: (options?: {
        query?: {
          page?: string
          limit?: string
          includeTeam?: string
          onlyWithVisibleMessages?: string
        }
      }) => Promise<Response>
    }
    'all-active': {
      $get: (options?: { query?: { teamId: string } }) => Promise<Response>
    }
    contacts: {
      $get: (options?: {
        query?: {
          page?: string
          limit?: string
          search?: string
          withPhoneOnly?: string
        }
      }) => Promise<Response>
    }
    handovers: {
      pending: {
        $get: (options?: { query?: { teamId: string } }) => Promise<Response>
      }
      ':requestId': {
        'available-agents': {
          $get: (options: {
            param: { requestId: number }
            query: { enforceTeamConstraints: string }
          }) => Promise<Response>
        }
        assign: {
          $post: (options: {
            param: { requestId: number }
            json: {}
          }) => Promise<Response>
        }
        cancel: {
          $post: (options: {
            param: { requestId: number }
          }) => Promise<Response>
        }
      }
    }
    'cancel-pending-handovers': {
      $post: () => Promise<Response>
    }
    ':sessionId': {
      'available-agents': {
        $get: (options: {
          param: { sessionId: string }
          query: { enforceTeamConstraints: string }
        }) => Promise<Response>
      }
      takeover: {
        $post: (options: {
          param: { sessionId: string }
          json?: SessionTakeoverOptions
        }) => Promise<Response>
      }
      transfer: {
        $post: (options: {
          param: { sessionId: string }
          json: {
            targetAgentId: number
            reason?: string
            teamId?: number
          }
        }) => Promise<Response>
        ':handoverRequestId': {
          cancel: {
            $post: (options: {
              param: { sessionId: string; handoverRequestId: string }
            }) => Promise<Response>
          }
        }
      }
      'transfer-status': {
        $get: (options: { param: { sessionId: string } }) => Promise<Response>
      }
      'available-agents-for-transfer': {
        $get: (options: { param: { sessionId: string } }) => Promise<Response>
      }
      translations: {
        $get: (options: {
          param: { sessionId: string }
          query?: { locale?: string }
        }) => Promise<Response>
      }
      messages: {
        ':messageId': {
          translation: {
            $get: (options: {
              param: { sessionId: string; messageId: string }
              query?: { locale?: string }
            }) => Promise<Response>
          }
        }
      }
    }
    visitors: {
      $get: (options?: { query?: { teamId: string } }) => Promise<Response>
    }
    reports: {
      conversations: {
        count: {
          $get: (options?: {
            query?: {
              dateRange?: string
              agent?: string
              userType?: string
            }
          }) => Promise<Response>
        }
      }
    }
    templates: {
      $get: (options?: {
        query?: {
          channel?: string
        }
      }) => Promise<Response>
      $post: (options: { json: MessageTemplatePayload }) => Promise<Response>
      ':templateId': {
        $put: (options: {
          param: { templateId: string }
          json: MessageTemplateUpdatePayload
        }) => Promise<Response>
        $delete: (options: {
          param: { templateId: string }
        }) => Promise<Response>
      }
    }
    channels: {
      facebook: {
        contacts: {
          $get: () => Promise<Response>
        }
      }
      initiate: {
        $post: (options: {
          json: {
            channel: 'whatsapp' | 'facebook_messenger'
            recipient: string
            templateId?: number
            message?: string
            components?: WhatsAppTemplateComponent[]
          }
        }) => Promise<Response>
      }
    }
    'initiate-visitor-chat': {
      $post: (options: { json: { visitorId: string } }) => Promise<Response>
    }
  }

  return hc(baseUrl, { fetch: fetchWithAuth }) as unknown as SessionsClient
}

async function handleHonoResponse<T>(
  response: Response,
  transform?: (payload: JsonObject | null) => T
): Promise<ApiResponse<T>> {
  if (response.status === 204) {
    return {
      status: response.status,
      success: true,
      data: undefined as unknown as T,
    }
  }

  let rawPayload: unknown = null
  try {
    rawPayload = await response.json()
  } catch (error) {
    rawPayload = null
  }

  const payload = isJsonObject(rawPayload) ? rawPayload : null

  if (!response.ok) {
    const messageFromPayload =
      getTrimmedString(payload?.message) ?? getTrimmedString(payload?.error)

    const errorMessage =
      messageFromPayload || `HTTP error! status: ${response.status}`

    throw new Error(errorMessage)
  }

  const data = transform
    ? transform(payload)
    : hasDataProperty(payload)
      ? (payload.data as T)
      : (rawPayload as T)

  const successFlag = toOptionalBoolean(payload?.success)
  const messageField = toOptionalString(payload?.message)
  const errorField = toOptionalString(payload?.error)

  return {
    status: response.status,
    success: successFlag ?? true,
    data,
    message: messageField,
    error: errorField,
  }
}

// Search API functions
export const searchAPI = {
  // Basic search
  search: async (
    params: SearchParams
  ): Promise<ApiResponse<SearchResult[]>> => {
    const searchParams = new URLSearchParams()

    if (params.query) {
      searchParams.append('q', params.query)
    }
    if (params.status) {
      searchParams.append('status', params.status)
    }
    if (params.category) {
      searchParams.append('category', params.category)
    }
    if (params.country) {
      searchParams.append('country', params.country)
    }
    if (params.page) {
      searchParams.append('page', params.page.toString())
    }
    if (params.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    if (params.sortBy) {
      searchParams.append('sortBy', params.sortBy)
    }
    if (params.sortOrder) {
      searchParams.append('sortOrder', params.sortOrder)
    }

    return apiRequest<SearchResult[]>(`/search?${searchParams.toString()}`)
  },

  // Advanced search with filters
  advancedSearch: async (
    query: string,
    filters: Record<string, JsonValue>
  ): Promise<ApiResponse<SearchResult[]>> => {
    return apiRequest<SearchResult[]>('/search', {
      method: 'POST',
      body: JSON.stringify({ query, filters }),
    })
  },
}

// Announcements API functions
export const announcementsAPI = {
  // Get all announcements
  getAll: async (
    params: {
      category?: string
      featured?: boolean
      page?: number
      limit?: number
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    } = {}
  ): Promise<ApiResponse<Announcement[]>> => {
    const searchParams = new URLSearchParams()

    if (params.category) {
      searchParams.append('category', params.category)
    }
    if (params.featured !== undefined) {
      searchParams.append('featured', params.featured.toString())
    }
    if (params.page) {
      searchParams.append('page', params.page.toString())
    }
    if (params.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    if (params.sortBy) {
      searchParams.append('sortBy', params.sortBy)
    }
    if (params.sortOrder) {
      searchParams.append('sortOrder', params.sortOrder)
    }

    return apiRequest<Announcement[]>(
      `/announcements?${searchParams.toString()}`
    )
  },

  // Get single announcement
  getById: async (id: string): Promise<ApiResponse<Announcement>> => {
    return apiRequest<Announcement>(`/announcements/${id}`)
  },

  // Create announcement
  create: async (
    announcement: Omit<Announcement, 'id' | 'date'>
  ): Promise<ApiResponse<Announcement>> => {
    return apiRequest<Announcement>('/announcements', {
      method: 'POST',
      body: JSON.stringify(announcement),
    })
  },

  // Update announcement
  update: async (
    id: string,
    announcement: Partial<Announcement>
  ): Promise<ApiResponse<Announcement>> => {
    return apiRequest<Announcement>(`/announcements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(announcement),
    })
  },

  // Delete announcement
  delete: async (id: string): Promise<ApiResponse<void>> => {
    return apiRequest<void>(`/announcements/${id}`, {
      method: 'DELETE',
    })
  },
}

// News API functions
export const newsAPI = {
  // Get all news
  getAll: async (
    params: {
      category?: string
      search?: string
      page?: number
      limit?: number
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    } = {}
  ): Promise<ApiResponse<NewsItem[]>> => {
    const searchParams = new URLSearchParams()

    if (params.category) {
      searchParams.append('category', params.category)
    }
    if (params.search) {
      searchParams.append('search', params.search)
    }
    if (params.page) {
      searchParams.append('page', params.page.toString())
    }
    if (params.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    if (params.sortBy) {
      searchParams.append('sortBy', params.sortBy)
    }
    if (params.sortOrder) {
      searchParams.append('sortOrder', params.sortOrder)
    }

    return apiRequest<NewsItem[]>(`/news?${searchParams.toString()}`).then(
      r => r
    )
  },

  // Get single news item
  getById: async (id: string): Promise<ApiResponse<NewsItem>> => {
    return apiRequest<NewsItem>(`/news/${id}`)
  },

  // Create news item
  create: async (
    newsItem: Omit<NewsItem, 'id' | 'date'>
  ): Promise<ApiResponse<NewsItem>> => {
    return apiRequest<NewsItem>('/news', {
      method: 'POST',
      body: JSON.stringify(newsItem),
    })
  },

  // Update news item
  update: async (
    id: string,
    newsItem: Partial<NewsItem>
  ): Promise<ApiResponse<NewsItem>> => {
    return apiRequest<NewsItem>(`/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(newsItem),
    })
  },

  // Delete news item
  delete: async (id: string): Promise<ApiResponse<void>> => {
    return apiRequest<void>(`/news/${id}`, {
      method: 'DELETE',
    })
  },
}

// Chat Session Types
interface ChatMessage {
  id: string
  role: 'system' | 'user' | 'assistant' | 'agent'
  content: string
  timestamp: string
  agentId?: number
  agentName?: string
  imageUrl?: string
  attachments?: FlowAttachment[]
  audioUrl?: string
  fileUrl?: string
  fileName?: string
}

interface ChatSession {
  id: string
  userId?: string
  displayName?: string
  platform: string
  platformId?: string
  status: string
  isHandedOver: boolean
  createdAt: string
  lastMessageAt?: string
  messages: ChatMessage[]
  agentId?: number
  agentName?: string
  teamId?: number | null
  teamName?: string | null
  assignment?: {
    agentId?: number | null
    agentName?: string | null
    teamId?: number | null
    teamName?: string | null
    status?: string | null
    requestId?: number | null
    assignedAt?: string | null
    requestedAt?: string | null
  } | null
  messageCount?: number
}

interface SessionStats {
  totalSessions: number
  activeSessions: number
  pendingHandovers: number
  completedToday: number
}

export interface AdminDashboardStatsPayload {
  sessions: {
    total: number
    active: number
    pendingHandover: number
    closed: number
    change: number
  }
  messages: {
    total: number
    today: number
    change: number
  }
  agents: {
    total: number
    active: number
    avgResponseTime: number
  }
  channels: {
    whatsapp: number
    facebook: number
    telegram: number
    instagram: number
    web: number
  }
  messageTrends: Array<{
    date: string
    sent: number
    received: number
  }>
  agentPerformance: Array<{
    name: string
    sessions: number
    avgResponseTime: number
  }>
}

// Export default API object
export const api = {
  // Generic HTTP methods
  get: <T = unknown>(endpoint: string): Promise<ApiResponse<T>> =>
    apiRequest<T>(endpoint),

  post: <TResponse = unknown, TBody extends JsonValue | FormData = JsonObject>(
    endpoint: string,
    data?: TBody
  ): Promise<ApiResponse<TResponse>> =>
    apiRequest<TResponse>(endpoint, {
      method: 'POST',
      body:
        data === undefined
          ? undefined
          : data instanceof FormData
            ? data
            : JSON.stringify(data),
    }),

  put: <TResponse = unknown, TBody extends JsonValue | FormData = JsonObject>(
    endpoint: string,
    data?: TBody
  ): Promise<ApiResponse<TResponse>> =>
    apiRequest<TResponse>(endpoint, {
      method: 'PUT',
      body:
        data === undefined
          ? undefined
          : data instanceof FormData
            ? data
            : JSON.stringify(data),
    }),

  delete: <T = unknown>(endpoint: string): Promise<ApiResponse<T>> =>
    apiRequest<T>(endpoint, { method: 'DELETE' }),

  patch: <TResponse = unknown, TBody extends JsonValue | FormData = JsonObject>(
    endpoint: string,
    data?: TBody
  ): Promise<ApiResponse<TResponse>> =>
    apiRequest<TResponse>(endpoint, {
      method: 'PATCH',
      body:
        data === undefined
          ? undefined
          : data instanceof FormData
            ? data
            : JSON.stringify(data),
    }),

  // Team Management for Agents
  teams: {
    list: (): Promise<ApiResponse<Team[]>> => {
      return apiRequest<Team[]>(`/admin/teams`)
    },
    getById: (id: number): Promise<ApiResponse<Team>> =>
      apiRequest<Team>(`/admin/teams/${id}`),
    getMyTeams: (): Promise<
      ApiResponse<Array<{ id: number; name: string; isActive: boolean }>>
    > =>
      apiRequest<Array<{ id: number; name: string; isActive: boolean }>>(
        `/agent/teams`
      ),
  },

  search: searchAPI,
  announcements: announcementsAPI,
  news: newsAPI,
  auth: {
    requestAdminPasswordReset: (
      identifier: string
    ): Promise<ApiResponse<PasswordResetResponsePayload>> =>
      apiRequest<PasswordResetResponsePayload>(
        '/admin/password/reset-request',
        {
          method: 'POST',
          body: JSON.stringify({ identifier }),
        }
      ),
    resetAdminPassword: (
      data: PasswordResetSubmitPayload
    ): Promise<ApiResponse<{ success: boolean; message?: string }>> =>
      apiRequest<{ success: boolean; message?: string }>(
        '/admin/password/reset',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      ),
    requestAgentPasswordReset: (
      identifier: string
    ): Promise<ApiResponse<PasswordResetResponsePayload>> =>
      apiRequest<PasswordResetResponsePayload>(
        '/agent/password/reset-request',
        {
          method: 'POST',
          body: JSON.stringify({ identifier }),
        }
      ),
    resetAgentPassword: (
      data: PasswordResetSubmitPayload
    ): Promise<ApiResponse<{ success: boolean; message?: string }>> =>
      apiRequest<{ success: boolean; message?: string }>(
        '/agent/password/reset',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      ),
  },
  users: {
    otp: {
      options: (): Promise<ApiResponse<ProfileOtpOptions>> =>
        apiRequest<ProfileOtpOptions>('/users/profile/otp/options'),
      request: (
        payload: ProfileOtpRequestPayload
      ): Promise<ApiResponse<ProfileOtpRequestResponse>> =>
        apiRequest<ProfileOtpRequestResponse>('/users/profile/otp/request', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      verify: (
        payload: ProfileOtpVerifyPayload
      ): Promise<ApiResponse<ProfileOtpVerifyResponse>> =>
        apiRequest<ProfileOtpVerifyResponse>('/users/profile/otp/verify', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
    },
    wallets: {
      list: (): Promise<ApiResponse<UserWallet[]>> =>
        apiRequest<UserWallet[]>('/users/wallets'),
    },
  },
  erpnext: {
    products: {
      list: (
        params: ERPNextProductListParams = {},
        configId?: number
      ): Promise<ApiResponse<ERPNextProductListResponse>> => {
        const query = new URLSearchParams()

        if (configId) query.set('configId', String(configId))
        if (params.search) {
          query.set('search', params.search)
        }
        if (params.category) {
          query.set('category', params.category)
        }
        if (params.status) {
          query.set('status', params.status)
        }
        if (params.itemType) {
          query.set('itemType', params.itemType)
        }
        if (typeof params.limit === 'number') {
          query.set('limit', String(params.limit))
        }
        if (typeof params.offset === 'number') {
          query.set('offset', String(params.offset))
        }
        if (params.sortBy) {
          query.set('sortBy', params.sortBy)
        }
        if (params.sortOrder) {
          query.set('sortOrder', params.sortOrder)
        }

        const qs = query.toString()
        const endpoint = qs
          ? `/admin/erpnext/products?${qs}`
          : '/admin/erpnext/products'
        return apiRequest<ERPNextProductListResponse>(endpoint)
      },
      get: (
        id: string,
        configId?: number
      ): Promise<ApiResponse<ERPNextProduct>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextProduct>(
          `/admin/erpnext/products/${encodeURIComponent(id)}${query}`
        )
      },
      create: (
        payload: ERPNextProductPayload,
        configId?: number
      ): Promise<ApiResponse<ERPNextProduct>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextProduct>(`/admin/erpnext/products${query}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      },
      update: (
        id: string,
        payload: Partial<ERPNextProductPayload>,
        configId?: number
      ): Promise<ApiResponse<ERPNextProduct>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextProduct>(
          `/admin/erpnext/products/${encodeURIComponent(id)}${query}`,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          }
        )
      },
      delete: (id: string, configId?: number): Promise<ApiResponse<void>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<void>(
          `/admin/erpnext/products/${encodeURIComponent(id)}${query}`,
          {
            method: 'DELETE',
          }
        ).then(r => r)
      },
    },
    templates: {
      create: (
        payload: ERPNextTemplatePayload,
        configId?: number
      ): Promise<ApiResponse<ERPNextProduct>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextProduct>(`/admin/erpnext/templates${query}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      },
      generateVariants: (
        id: string,
        payload: ERPNextGenerateVariantsPayload,
        configId?: number
      ): Promise<
        ApiResponse<{ variants: ERPNextProduct[]; count: number }>
      > => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<{ variants: ERPNextProduct[]; count: number }>(
          `/admin/erpnext/templates/${encodeURIComponent(id)}/generate-variants${query}`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
          }
        )
      },
    },
    variants: {
      create: (
        payload: ERPNextVariantPayload,
        configId?: number
      ): Promise<ApiResponse<ERPNextProduct>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextProduct>(`/admin/erpnext/variants${query}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      },
    },
    attributes: {
      list: (
        configId?: number
      ): Promise<ApiResponse<{ attributes: ERPNextAttribute[] }>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<{ attributes: ERPNextAttribute[] }>(
          `/admin/erpnext/attributes${query}`
        )
      },
    },
    customers: {
      list: (
        params: ERPNextCustomerListParams = {},
        configId?: number
      ): Promise<ApiResponse<ERPNextCustomerListResponse>> => {
        const query = new URLSearchParams()

        if (configId) query.set('configId', String(configId))
        if (params.search) {
          query.set('search', params.search)
        }
        if (params.status && params.status !== 'all') {
          query.set('status', params.status)
        }
        if (params.type) {
          query.set('type', params.type)
        }
        if (params.group) {
          query.set('group', params.group)
        }
        if (typeof params.limit === 'number') {
          query.set('limit', String(params.limit))
        }
        if (typeof params.offset === 'number') {
          query.set('offset', String(params.offset))
        }
        if (params.sortBy) {
          query.set('sortBy', params.sortBy)
        }
        if (params.sortOrder) {
          query.set('sortOrder', params.sortOrder)
        }

        const qs = query.toString()
        const endpoint = qs
          ? `/admin/erpnext/customers?${qs}`
          : '/admin/erpnext/customers'
        return apiRequest<ERPNextCustomerListResponse>(endpoint)
      },
      get: (
        id: string,
        configId?: number
      ): Promise<ApiResponse<ERPNextCustomer>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextCustomer>(
          `/admin/erpnext/customers/${encodeURIComponent(id)}${query}`
        )
      },
      create: (
        payload: ERPNextCustomerPayload,
        configId?: number
      ): Promise<ApiResponse<ERPNextCustomer>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextCustomer>(`/admin/erpnext/customers${query}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      },
      update: (
        id: string,
        payload: Partial<ERPNextCustomerPayload>,
        configId?: number
      ): Promise<ApiResponse<ERPNextCustomer>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextCustomer>(
          `/admin/erpnext/customers/${encodeURIComponent(id)}${query}`,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          }
        )
      },
      delete: (id: string, configId?: number): Promise<ApiResponse<void>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<void>(
          `/admin/erpnext/customers/${encodeURIComponent(id)}${query}`,
          {
            method: 'DELETE',
          }
        ).then(r => r)
      },
    },
    customerAddresses: {
      list: (
        customerId: string,
        params: {
          limit?: number
          offset?: number
          sortBy?: string
          sortOrder?: 'asc' | 'desc'
        } = {},
        configId?: number
      ): Promise<ApiResponse<ERPNextAddressListResponse>> => {
        const query = new URLSearchParams()

        if (configId) query.set('configId', String(configId))
        if (typeof params.limit === 'number') {
          query.set('limit', String(params.limit))
        }
        if (typeof params.offset === 'number') {
          query.set('offset', String(params.offset))
        }
        if (params.sortBy) {
          query.set('sortBy', params.sortBy)
        }
        if (params.sortOrder) {
          query.set('sortOrder', params.sortOrder)
        }

        const qs = query.toString()
        const endpoint = qs
          ? `/admin/erpnext/customers/${encodeURIComponent(customerId)}/addresses?${qs}`
          : `/admin/erpnext/customers/${encodeURIComponent(customerId)}/addresses`
        return apiRequest<ERPNextAddressListResponse>(endpoint)
      },
      create: (
        customerId: string,
        payload: ERPNextAddressPayload,
        configId?: number
      ): Promise<ApiResponse<ERPNextAddress>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextAddress>(
          `/admin/erpnext/customers/${encodeURIComponent(customerId)}/addresses${query}`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
          }
        )
      },
      update: (
        customerId: string,
        addressId: string,
        payload: Partial<ERPNextAddressPayload>,
        configId?: number
      ): Promise<ApiResponse<ERPNextAddress>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextAddress>(
          `/admin/erpnext/customers/${encodeURIComponent(customerId)}/addresses/${encodeURIComponent(addressId)}${query}`,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          }
        )
      },
      delete: (
        customerId: string,
        addressId: string,
        configId?: number
      ): Promise<ApiResponse<void>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<void>(
          `/admin/erpnext/customers/${encodeURIComponent(customerId)}/addresses/${encodeURIComponent(addressId)}${query}`,
          {
            method: 'DELETE',
          }
        ).then(r => r)
      },
    },
    orders: {
      list: (
        params: ERPNextOrderListParams = {},
        configId?: number
      ): Promise<ApiResponse<ERPNextOrderListResponse>> => {
        const query = new URLSearchParams()

        if (configId) query.set('configId', String(configId))
        if (params.customer) {
          query.set('customer', params.customer)
        }
        if (params.status) {
          query.set('status', params.status)
        }
        if (typeof params.limit === 'number') {
          query.set('limit', String(params.limit))
        }
        if (typeof params.offset === 'number') {
          query.set('offset', String(params.offset))
        }
        if (params.sortBy) {
          query.set('sortBy', params.sortBy)
        }
        if (params.sortOrder) {
          query.set('sortOrder', params.sortOrder)
        }

        const qs = query.toString()
        const endpoint = qs
          ? `/admin/erpnext/orders?${qs}`
          : '/admin/erpnext/orders'
        return apiRequest<ERPNextOrderListResponse>(endpoint)
      },
      get: (
        id: string,
        configId?: number
      ): Promise<ApiResponse<ERPNextOrder>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextOrder>(
          `/admin/erpnext/orders/${encodeURIComponent(id)}${query}`
        )
      },
      create: (
        payload: ERPNextOrderPayload,
        configId?: number
      ): Promise<ApiResponse<ERPNextOrder>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextOrder>(`/admin/erpnext/orders${query}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      },
      update: (
        id: string,
        payload: Partial<ERPNextOrderPayload> & { items?: ERPNextOrderItem[] },
        configId?: number
      ): Promise<ApiResponse<ERPNextOrder>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<ERPNextOrder>(
          `/admin/erpnext/orders/${encodeURIComponent(id)}${query}`,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          }
        )
      },
      delete: (id: string, configId?: number): Promise<ApiResponse<void>> => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<void>(
          `/admin/erpnext/orders/${encodeURIComponent(id)}${query}`,
          {
            method: 'DELETE',
          }
        ).then(r => r)
      },
    },
    warehouses: {
      list: (
        params?: {
          search?: string
        },
        configId?: number
      ): Promise<ApiResponse<ERPNextWarehouseListResponse>> => {
        const query = new URLSearchParams()

        if (configId) query.set('configId', String(configId))
        if (params?.search) {
          query.set('search', params.search)
        }

        const qs = query.toString()
        const endpoint = qs
          ? `/admin/erpnext/warehouses?${qs}`
          : '/admin/erpnext/warehouses'
        return apiRequest<ERPNextWarehouseListResponse>(endpoint)
      },
    },
    companies: {
      search: (
        query: string,
        limit: number = 5,
        configId?: number
      ): Promise<ApiResponse<{ companies: Array<{ name: string }> }>> => {
        const params = new URLSearchParams()
        if (configId) params.set('configId', String(configId))
        params.set('query', query)
        params.set('limit', String(limit))
        return apiRequest<{ companies: Array<{ name: string }> }>(
          `/admin/erpnext/companies/search?${params.toString()}`
        )
      },
    },
    uoms: {
      list: (
        configId?: number
      ): Promise<
        ApiResponse<{
          uoms: Array<{ name: string; uom_name?: string; enabled?: number }>
        }>
      > => {
        const query = configId ? `?configId=${configId}` : ''
        return apiRequest<{
          uoms: Array<{ name: string; uom_name?: string; enabled?: number }>
        }>(`/admin/erpnext/uoms${query}`)
      },
    },
  },
  admin: {
    accessControl: {
      listRoles: (): Promise<ApiResponse<RoleDefinition[]>> =>
        apiRequest<RoleDefinition[]>(`/admin/access-control/roles`),
      listPermissions: (params?: {
        roleCode?: string | null
        roleCodes?: string[] | null
        code?: string | null
      }): Promise<ApiResponse<Permission[]>> => {
        const query = new URLSearchParams()

        const normalizedRoleCodes = Array.from(
          new Set(
            (params?.roleCodes ?? []).filter((code): code is string =>
              Boolean(code && code.trim().length > 0)
            )
          )
        )

        if (normalizedRoleCodes.length > 0) {
          normalizedRoleCodes.forEach(code => {
            query.append('role', code)
          })
        } else if (params?.roleCode && params.roleCode.trim().length > 0) {
          query.set('role', params.roleCode)
        }

        if (params?.code && params.code.trim().length > 0) {
          query.set('code', params.code)
        }

        const qs = query.toString()

        const endpoint = qs
          ? `/admin/access-control/permissions?${qs}`
          : `/admin/access-control/permissions`

        return apiRequest<Permission[]>(endpoint)
      },
      createPermission: (
        payload: PermissionCreatePayload
      ): Promise<ApiResponse<Permission>> =>
        apiRequest<Permission>(`/admin/access-control/permissions`, {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      updatePermission: (
        permissionId: number,
        payload: PermissionUpdatePayload
      ): Promise<ApiResponse<Permission>> =>
        apiRequest<Permission>(
          `/admin/access-control/permissions/${permissionId}`,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          }
        ),
      deletePermission: (permissionId: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/access-control/permissions/${permissionId}`, {
          method: 'DELETE',
        }).then(r => r),
    },
    // Admin Dashboard Metrics
    dashboard: {
      stats: (): Promise<ApiResponse<AdminDashboardStatsPayload>> =>
        apiRequest<AdminDashboardStatsPayload>('/admin/dashboard/stats'),
    },
    // Admin User Management
    getMe: (): Promise<ApiResponse<{ user: AdminUser }>> =>
      apiRequest<{ user: AdminUser }>('/admin/me'),
    listUsers: (params?: {
      search?: string
      page?: number
      limit?: number
      scope?: 'admin' | 'all'
      roles?: string[]
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
      siteId?: number | string
    }): Promise<ApiResponse<AdminUserResponse[]>> => {
      const searchParams = new URLSearchParams()
      if (params?.search) {
        searchParams.set('search', params.search)
      }
      if (params?.page) {
        searchParams.set('page', String(params.page))
      }
      if (params?.limit) {
        searchParams.set('limit', String(params.limit))
      }
      if (params?.scope) {
        searchParams.set('scope', params.scope)
      }
      if (params?.roles && params.roles.length > 0) {
        searchParams.set('roles', params.roles.join(','))
      }
      if (params?.sortBy) {
        searchParams.set('sortBy', params.sortBy)
      }
      if (params?.sortOrder) {
        searchParams.set('sortOrder', params.sortOrder)
      }
      if (params?.siteId !== undefined) {
        searchParams.set('siteId', String(params.siteId))
      }
      const qs = searchParams.toString()
      const url = qs ? `/admin/users?${qs}` : '/admin/users'
      return apiRequest<AdminUserResponse[]>(url).then(r => r)
    },
    getUserById: (
      id: number,
      options?: { scope?: 'admin' | 'all' }
    ): Promise<ApiResponse<AdminUserResponse>> => {
      const scope = options?.scope || 'all'
      return apiRequest<AdminUserResponse>(`/admin/users/${id}?scope=${scope}`)
    },
    createUser: (
      data: AdminUserCreationRequest
    ): Promise<ApiResponse<AdminUserResponse>> =>
      apiRequest<AdminUserResponse>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateUser: (
      id: number,
      data: AdminUserUpdateRequest
    ): Promise<ApiResponse<AdminUserResponse>> =>
      apiRequest<AdminUserResponse>(`/admin/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteUser: (id: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/users/${id}`, { method: 'DELETE' }).then(r => r),

    // User Mobile Management
    getUserMobiles: (userId: number): Promise<ApiResponse<UserMobile[]>> =>
      apiRequest<UserMobile[]>(`/admin/user-mobiles/users/${userId}/mobiles`),
    createUserMobile: (
      userId: number,
      data: UserMobileCreateRequest
    ): Promise<ApiResponse<{ success: boolean; data: any }>> =>
      apiRequest<{ success: boolean; data: any }>(
        `/admin/user-mobiles/users/${userId}/mobiles`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      ),
    updateUserMobile: (
      userId: number,
      id: number,
      data: UserMobileUpdateRequest
    ): Promise<ApiResponse<{ success: boolean; data: any }>> =>
      apiRequest<{ success: boolean; data: any }>(
        `/admin/user-mobiles/users/${userId}/mobiles/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      ),
    deleteUserMobile: (
      userId: number,
      id: number
    ): Promise<ApiResponse<{ success: boolean }>> =>
      apiRequest<{ success: boolean }>(
        `/admin/user-mobiles/users/${userId}/mobiles/${id}`,
        {
          method: 'DELETE',
        }
      ),

    // Site Management
    listSites: (): Promise<ApiResponse<AdminSite[]>> =>
      apiRequest<AdminSite[]>('/admin/sites'),
    getSite: (): Promise<ApiResponse<AdminSite>> =>
      apiRequest<AdminSite>('/admin/sites/current'),
    updateSite: (payload: SiteUpdateRequest): Promise<ApiResponse<AdminSite>> =>
      apiRequest<AdminSite>('/admin/sites/current', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),

    // Chat Session Management removed: sessions are agent-scoped now
    // (getSessions, getSession, completeSession, getHandoverRequests, getSessionStats)

    whatsapp: {
      getConfig: (): Promise<ApiResponse<WhatsAppConfigResponse>> =>
        apiRequest<WhatsAppConfigResponse>('/admin/whatsapp/config'),
      saveConfig: (
        payload: WhatsAppConfigPayload
      ): Promise<ApiResponse<{ message?: string }>> =>
        apiRequest<{ message?: string }>('/admin/whatsapp/config', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      getWebhookInfo: (): Promise<ApiResponse<WhatsAppWebhookInfo>> =>
        apiRequest<WhatsAppWebhookInfo>('/admin/whatsapp/webhook-url'),
      testConfig: (): Promise<ApiResponse<{ message?: string }>> =>
        apiRequest<{ message?: string }>('/admin/whatsapp/test', {
          method: 'POST',
        }),
    },

    fbWhatsApp: {
      list: (): Promise<ApiResponse<WhatsAppConfig[]>> =>
        apiRequest<WhatsAppConfig[]>('/admin/fb-whatsapp/list'),
    },

    // S3 Configuration Management
    listS3Configurations: (): Promise<
      ApiResponse<S3ConfigurationResponse[]>
    > => {
      const qs = ''
      return apiRequest<S3ConfigurationResponse[]>(
        `/admin/s3-configurations${qs}`
      )
    },
    getS3ConfigurationById: (
      id: number
    ): Promise<ApiResponse<S3ConfigurationResponse>> =>
      apiRequest<S3ConfigurationResponse>(`/admin/s3-configurations/${id}`),
    createS3Configuration: (
      data: S3ConfigurationCreationRequest
    ): Promise<ApiResponse<S3ConfigurationResponse>> =>
      apiRequest<S3ConfigurationResponse>('/admin/s3-configurations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateS3Configuration: (
      id: number,
      data: S3ConfigurationUpdateRequest
    ): Promise<ApiResponse<S3ConfigurationResponse>> =>
      apiRequest<S3ConfigurationResponse>(`/admin/s3-configurations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    pushS3CorsConfiguration: (
      id: number,
      data?: { corsConfiguration?: S3CorsRule[] | null }
    ): Promise<ApiResponse<{ corsRulesCount: number }>> =>
      apiRequest<{ corsRulesCount: number }>(
        `/admin/s3-configurations/${id}/push-cors`,
        {
          method: 'POST',
          body: JSON.stringify(data ?? {}),
        }
      ),
    testS3Configuration: (id: number): Promise<ApiResponse<S3TestResult>> =>
      apiRequest<S3TestResult>(`/admin/s3-configurations/${id}/test`, {
        method: 'POST',
      }),
    deleteS3Configuration: (id: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/s3-configurations/${id}`, {
        method: 'DELETE',
      }),
    getS3Presets: (): Promise<
      ApiResponse<Record<string, S3ConfigurationPreset>>
    > =>
      // Backend uses S3ConfigurationPreset from server types
      apiRequest<Record<string, S3ConfigurationPreset>>(
        '/admin/s3-configurations/presets'
      ),

    // ERPNext Configuration Management
    getErpnextConfig: (): Promise<ApiResponse<ErpnextConfig | null>> =>
      apiRequest<ErpnextConfig | null>('/admin/erpnext/config'),
    getAllErpnextConfigs: (): Promise<ApiResponse<ErpnextConfig[]>> =>
      apiRequest<ErpnextConfig[]>('/admin/erpnext/configs'),
    createErpnextConfig: (
      data: ErpnextConfigPayload
    ): Promise<ApiResponse<ErpnextConfig>> =>
      apiRequest<ErpnextConfig>('/admin/erpnext/config', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateErpnextConfig: (
      id: number,
      data: Partial<ErpnextConfigPayload>
    ): Promise<ApiResponse<ErpnextConfig>> =>
      apiRequest<ErpnextConfig>(`/admin/erpnext/config/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteErpnextConfig: (id: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/erpnext/config/${id}`, {
        method: 'DELETE',
      }),
    testErpnextConfig: (
      id: number
    ): Promise<ApiResponse<{ status: string; message?: string }>> =>
      apiRequest<{ status: string; message?: string }>(
        `/admin/erpnext/configs/${id}/test`,
        {
          method: 'POST',
        }
      ),
    testErpnextConnection: (): Promise<ApiResponse<{ status: string }>> =>
      apiRequest<{ status: string }>('/admin/erpnext/health'),

    // Collection Management
    getCollections: (params?: {
      q?: string
      page?: number
      limit?: number
      sort?: string
      order?: 'asc' | 'desc'
    }): Promise<ApiResponse<Collection[]>> => {
      const search = new URLSearchParams()
      if (params?.q) search.set('q', params.q)
      if (params?.page) search.set('page', String(params.page))
      if (params?.limit) search.set('limit', String(params.limit))
      if (params?.sort) search.set('sort', params.sort)
      if (params?.order) search.set('order', params.order)
      const qs = search.toString()
      return apiRequest<CollectionListResponse>(
        `/admin/collections${qs ? `?${qs}` : ''}`
      ).then(response => ({
        ...response,
        data: ensureCollectionArray(response.data),
      }))
    },
    getCollectionById: (
      id: number | string
    ): Promise<ApiResponse<Collection>> =>
      apiRequest<Collection>(`/admin/collections/${id}`),
    createCollection: (
      data: CollectionCreateRequest
    ): Promise<ApiResponse<Collection>> =>
      apiRequest<Collection>('/admin/collections', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateCollection: (
      id: number | string,
      data: CollectionUpdateRequest
    ): Promise<ApiResponse<Collection>> =>
      apiRequest<Collection>(`/admin/collections/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteCollection: (id: number | string): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/collections/${id}`, { method: 'DELETE' }).then(
        r => r
      ),

    // Document Management
    getDocuments: (
      page = 1,
      limit = 10
    ): Promise<ApiResponse<PaginatedResponse<Document>>> => {
      const qs = `page=${page}&limit=${limit}`
      return apiRequest<PaginatedResponse<Document>>(`/admin/documents?${qs}`)
    },
    getDocumentsByCollection: (
      collectionId: string,
      page = 1,
      limit = 10,
      signal?: AbortSignal
    ): Promise<ApiResponse<PaginatedResponse<Document>>> => {
      const qs = `page=${page}&limit=${limit}`
      return apiRequest<PaginatedResponse<Document>>(
        `/admin/collections/${collectionId}/documents?${qs}`,
        { signal }
      )
    },
    getDocumentById: (id: string): Promise<Document | undefined> =>
      apiRequest<Document>(`/admin/documents/${id}`).then(r => r.data),
    getDocumentPresignedUrl: (
      id: string,
      options: { expiresIn?: number } = {}
    ): Promise<
      ApiResponse<{
        presignedUrl: string
        url?: string | null
        expiresIn: number
      }>
    > => {
      const sp = new URLSearchParams()
      if (
        typeof options.expiresIn === 'number' &&
        Number.isFinite(options.expiresIn)
      ) {
        const clamped = Math.max(60, Math.min(options.expiresIn, 60 * 60))
        sp.set('expires', String(clamped))
      }
      const qs = sp.toString()
      return apiRequest<{
        presignedUrl: string
        url?: string | null
        expiresIn: number
      }>(
        `/admin/documents/${encodeURIComponent(id)}/presign${qs ? `?${qs}` : ''}`
      )
    },
    uploadDocument: async (
      params: {
        collectionId: string
        file: File
        name?: string
        description?: string
        tags?: string[]
        metadata?: Record<string, any>
      },
      onProgress?: (progress: number) => void
    ): Promise<ApiResponse<DocumentUploadResponse>> => {
      const { collectionId, file, name, description, tags, metadata } = params
      const trimmedCollectionId =
        typeof collectionId === 'string' && collectionId.trim().length > 0
          ? collectionId.trim()
          : ''

      if (!trimmedCollectionId) {
        return {
          success: false,
          status: 400,
          error: 'Collection ID is required for document upload',
        }
      }

      if (!(file instanceof File)) {
        return {
          success: false,
          status: 400,
          error: 'A document file must be provided for upload',
        }
      }

      // Site prefix is added by the backend presign endpoint using the
      // authoritative siteId resolved from the request context.
      const sanitizedFilename =
        file.name && file.name.trim().length > 0
          ? file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
          : 'document'
      const timestamp = Date.now()
      const s3Key = `collections/${trimmedCollectionId}/${timestamp}_${sanitizedFilename}`

      try {
        const uploadResponse = await uploadAPI.uploadFileViaPresignedUrl(
          file,
          progress => {
            if (typeof onProgress === 'function') {
              const clamped = Math.max(0, Math.min(progress, 100))
              const scaled = Math.round(clamped * 0.9)
              onProgress(scaled)
            }
          },
          {
            s3Key,
            contentType: file.type || 'application/octet-stream',
            presignExpiresIn: 15 * 60,
          }
        )

        if (!uploadResponse.success || !uploadResponse.data?.s3Key) {
          return {
            success: false,
            status: uploadResponse.status,
            error:
              uploadResponse.error ||
              'Failed to upload file to storage. Please try again.',
          }
        }

        const finalizePayload: Record<string, unknown> = {
          collectionId: trimmedCollectionId,
          s3Key: uploadResponse.data.s3Key,
          originalFilename: file.name,
          mimetype: file.type,
          size: file.size,
        }

        if (typeof name === 'string' && name.trim().length > 0) {
          finalizePayload.name = name.trim()
        }

        if (typeof description === 'string' && description.trim().length > 0) {
          finalizePayload.description = description
        }

        if (Array.isArray(tags) && tags.length > 0) {
          finalizePayload.tags = tags
        }

        if (metadata && typeof metadata === 'object') {
          finalizePayload.metadata = metadata
        }

        const finalizeResponse = await apiRequest<DocumentUploadResponse>(
          '/admin/documents/upload/finalize',
          {
            method: 'POST',
            body: JSON.stringify(finalizePayload),
          }
        )

        if (finalizeResponse.success) {
          onProgress?.(100)
        }

        return finalizeResponse
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Unexpected error while uploading document'
        return {
          success: false,
          status: 500,
          error: message,
        }
      }
    },
    deleteDocument: (id: string): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/documents/${id}`, { method: 'DELETE' }).then(
        r => r
      ),

    // Knowledge Skills
    listKnowledgeSkillCollections: (): Promise<
      ApiResponse<KnowledgeSkillCollection[]>
    > =>
      apiRequest<KnowledgeSkillCollection[]>(
        '/admin/knowledge-skills/collections'
      ),
    createKnowledgeSkillCollection: (
      payload: KnowledgeSkillCollectionInput
    ): Promise<ApiResponse<KnowledgeSkillCollection>> =>
      apiRequest<KnowledgeSkillCollection>(
        '/admin/knowledge-skills/collections',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ),
    updateKnowledgeSkillCollection: (
      collectionId: number,
      payload: Partial<KnowledgeSkillCollectionInput>
    ): Promise<ApiResponse<KnowledgeSkillCollection>> =>
      apiRequest<KnowledgeSkillCollection>(
        `/admin/knowledge-skills/collections/${collectionId}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      ),
    deleteKnowledgeSkillCollection: (
      collectionId: number
    ): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/knowledge-skills/collections/${collectionId}`, {
        method: 'DELETE',
      }).then(r => r),
    listKnowledgeSkills: (params?: {
      collectionId?: number | string
      parentId?: number | null
    }): Promise<ApiResponse<KnowledgeSkill[]>> => {
      const sp = new URLSearchParams()
      if (params?.collectionId !== undefined) {
        sp.set('collectionId', String(params.collectionId))
      }
      if (params?.parentId === null) {
        sp.set('parentId', 'null')
      } else if (params?.parentId !== undefined) {
        sp.set('parentId', String(params.parentId))
      }
      const qs = sp.toString()
      return apiRequest<KnowledgeSkill[]>(
        `/admin/knowledge-skills/skills${qs ? `?${qs}` : ''}`
      )
    },
    createKnowledgeSkill: (
      payload: KnowledgeSkillInput
    ): Promise<ApiResponse<KnowledgeSkill>> =>
      apiRequest<KnowledgeSkill>('/admin/knowledge-skills/skills', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    updateKnowledgeSkill: (
      skillId: number,
      payload: Partial<KnowledgeSkillInput>
    ): Promise<ApiResponse<KnowledgeSkill>> =>
      apiRequest<KnowledgeSkill>(`/admin/knowledge-skills/skills/${skillId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    deleteKnowledgeSkill: (skillId: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/knowledge-skills/skills/${skillId}`, {
        method: 'DELETE',
      }).then(r => r),
    processKnowledgeSkillDocument: (
      payload: KnowledgeSkillDocumentInput
    ): Promise<ApiResponse<KnowledgeSkillDocumentProcessResult>> =>
      apiRequest<KnowledgeSkillDocumentProcessResult>(
        '/admin/knowledge-skills/documents/process',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ),
    processKnowledgeSkillDocumentUpload: (
      formData: FormData
    ): Promise<ApiResponse<KnowledgeSkillDocumentProcessResult>> =>
      apiRequest<KnowledgeSkillDocumentProcessResult>(
        '/admin/knowledge-skills/documents/process',
        {
          method: 'POST',
          body: formData,
        }
      ),
    searchKnowledgeSkills: (params: {
      query: string
      collectionIds?: Array<number | string>
    }): Promise<ApiResponse<KnowledgeSkillSearchResult[]>> => {
      const sp = new URLSearchParams()
      sp.set('query', params.query)
      if (params.collectionIds && params.collectionIds.length > 0) {
        const normalized = params.collectionIds
          .map(id => String(id).trim())
          .filter(Boolean)
        if (normalized.length > 0) {
          sp.set('collectionIds', normalized.join(','))
        }
      }
      return apiRequest<KnowledgeSkillSearchResult[]>(
        `/admin/knowledge-skills/search?${sp.toString()}`
      )
    },

    knowledgeSearch: (params: {
      query: string
      page?: number
      limit?: number
      collection_id?: string
      signal?: AbortSignal
    }): Promise<ApiResponse<KnowledgeSearchResponse>> => {
      const sp = new URLSearchParams()
      sp.set('query', params.query)
      sp.set('page', String(params.page ?? 1))
      sp.set('limit', String(params.limit ?? 5))
      if (params.collection_id) {
        sp.set('collection_id', params.collection_id)
      }
      return apiRequest<KnowledgeSearchResponse>(
        `/admin/documents/knowledge-search?${sp.toString()}`,
        {
          signal: params.signal,
        }
      )
    },
    // Service Management
    listServices: (): Promise<ApiResponse<ServiceConfiguration[]>> => {
      return apiRequest<ServiceConfiguration[]>(`/admin/services`).then(r => r)
    },
    getServiceById: (id: number): Promise<ApiResponse<ServiceConfiguration>> =>
      apiRequest<ServiceConfiguration>(`/admin/services/${id}`),
    createService: (
      data: ServiceCreationRequest
    ): Promise<ApiResponse<ServiceConfiguration>> =>
      apiRequest<ServiceConfiguration>('/admin/services', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateService: (
      id: number,
      data: ServiceUpdateRequest
    ): Promise<ApiResponse<ServiceConfiguration>> =>
      apiRequest<ServiceConfiguration>(`/admin/services/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteService: (id: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/services/${id}`, { method: 'DELETE' }).then(
        r => r
      ),

    croco: {
      balance: (params: {
        userWalletId: number
        currency?: string
      }): Promise<ApiResponse<CrocoBalanceResult>> => {
        const searchParams = new URLSearchParams()
        searchParams.set('userWalletId', String(params.userWalletId))
        if (params.currency) {
          searchParams.set('currency', params.currency)
        }
        const qs = searchParams.toString()
        const url = qs ? `/croco/balance?${qs}` : '/croco/balance'
        return apiRequest<CrocoBalanceResult>(url)
      },
      gameList: (params?: {
        serviceId?: number
        serviceName?: string
      }): Promise<ApiResponse<CrocoGameInfo[]>> => {
        const searchParams = new URLSearchParams()
        if (params?.serviceId) {
          searchParams.set('serviceId', String(params.serviceId))
        }
        if (params?.serviceName) {
          searchParams.set('serviceName', params.serviceName)
        }
        const qs = searchParams.toString()
        const url = qs ? `/croco/game-list?${qs}` : '/croco/game-list'
        return apiRequest<CrocoGameInfo[]>(url)
      },
      placeBet: (payload: {
        userWalletId: number
        gameId: number
        bet: number
        currency?: string
        transactionId?: string
      }): Promise<ApiResponse<CrocoPlaceBetResult>> =>
        apiRequest<CrocoPlaceBetResult>('/croco/place-bet', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      winningList: (params: {
        userWalletId: number
        gameId?: number
        days?: number
        limit?: number
        currency?: string
        result?: 'winning' | 'losing' | 'all'
      }): Promise<ApiResponse<CrocoWinningListResponse>> => {
        const searchParams = new URLSearchParams()
        searchParams.set('userWalletId', String(params.userWalletId))
        if (params.gameId !== undefined) {
          searchParams.set('gameId', String(params.gameId))
        }
        if (params.days !== undefined) {
          searchParams.set('days', String(params.days))
        }
        if (params.limit !== undefined) {
          searchParams.set('limit', String(params.limit))
        }
        if (params.currency) {
          searchParams.set('currency', params.currency)
        }
        if (params.result) {
          searchParams.set('result', params.result)
        }
        const qs = searchParams.toString()
        const url = qs ? `/croco/winning-list?${qs}` : '/croco/winning-list'
        return apiRequest<CrocoWinningListResponse>(url)
      },
    },

    wallets: {
      list: (params?: {
        status?: boolean
        q?: string
      }): Promise<ApiResponse<Wallet[]>> => {
        const searchParams = new URLSearchParams()
        if (params?.status !== undefined) {
          searchParams.set('status', params.status ? '1' : '0')
        }
        if (params?.q) {
          searchParams.set('q', params.q)
        }
        const qs = searchParams.toString()
        const url = qs ? `/admin/wallets?${qs}` : '/admin/wallets'
        return apiRequest<Wallet[]>(url)
      },
      detail: (id: number): Promise<ApiResponse<Wallet>> =>
        apiRequest<Wallet>(`/admin/wallets/${id}`),
      create: (data: WalletCreatePayload): Promise<ApiResponse<Wallet>> =>
        apiRequest<Wallet>('/admin/wallets', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: WalletUpdatePayload
      ): Promise<ApiResponse<Wallet>> =>
        apiRequest<Wallet>(`/admin/wallets/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      conversions: (
        walletId: number
      ): Promise<ApiResponse<WalletConversionRate[]>> =>
        apiRequest<WalletConversionRate[]>(
          `/admin/wallets/${walletId}/conversions`
        ),
      updateConversions: (
        walletId: number,
        rates: WalletConversionRateInput[]
      ): Promise<ApiResponse<WalletConversionRate[]>> =>
        apiRequest<WalletConversionRate[]>(
          `/admin/wallets/${walletId}/conversions`,
          {
            method: 'PUT',
            body: JSON.stringify({ rates }),
          }
        ),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/wallets/${id}`, { method: 'DELETE' }).then(
          r => r
        ),
    },

    walletProviders: {
      list: (params?: {
        status?: boolean
        q?: string
      }): Promise<ApiResponse<WalletProvider[]>> => {
        const searchParams = new URLSearchParams()
        if (params?.status !== undefined) {
          searchParams.set('status', String(params.status))
        }
        if (params?.q) {
          searchParams.set('q', params.q)
        }
        const qs = searchParams.toString()
        const url = qs
          ? `/admin/wallet-providers?${qs}`
          : '/admin/wallet-providers'
        return apiRequest<WalletProvider[]>(url)
      },
      detail: (id: number): Promise<ApiResponse<WalletProvider>> =>
        apiRequest<WalletProvider>(`/admin/wallet-providers/${id}`),
      create: (
        data: WalletProviderCreatePayload
      ): Promise<ApiResponse<WalletProvider>> =>
        apiRequest<WalletProvider>('/admin/wallet-providers', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: WalletProviderUpdatePayload
      ): Promise<ApiResponse<WalletProvider>> =>
        apiRequest<WalletProvider>(`/admin/wallet-providers/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/wallet-providers/${id}`, {
          method: 'DELETE',
        }).then(r => r),
    },

    userWallets: {
      list: (params?: {
        walletId?: number
        userId?: number
      }): Promise<ApiResponse<UserWallet[]>> => {
        const searchParams = new URLSearchParams()
        if (params?.walletId !== undefined) {
          searchParams.set('walletId', String(params.walletId))
        }
        if (params?.userId !== undefined) {
          searchParams.set('userId', String(params.userId))
        }
        const qs = searchParams.toString()
        const url = qs ? `/admin/user-wallets?${qs}` : '/admin/user-wallets'
        return apiRequest<UserWallet[]>(url)
      },
      detail: (id: number): Promise<ApiResponse<UserWallet>> =>
        apiRequest<UserWallet>(`/admin/user-wallets/${id}`),
      create: (
        data: UserWalletCreatePayload
      ): Promise<ApiResponse<UserWallet>> =>
        apiRequest<UserWallet>('/admin/user-wallets', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/user-wallets/${id}`, {
          method: 'DELETE',
        }).then(r => r),
    },

    rewards: {
      list: (params?: {
        status?: boolean
        walletId?: number
        q?: string
      }): Promise<ApiResponse<Reward[]>> => {
        const searchParams = new URLSearchParams()
        if (params?.status !== undefined) {
          searchParams.set('status', params.status ? '1' : '0')
        }
        if (params?.walletId !== undefined) {
          searchParams.set('walletId', String(params.walletId))
        }
        if (params?.q) {
          searchParams.set('q', params.q)
        }
        const qs = searchParams.toString()
        const url = qs ? `/admin/rewards?${qs}` : '/admin/rewards'
        return apiRequest<Reward[]>(url)
      },
      detail: (id: number): Promise<ApiResponse<Reward>> =>
        apiRequest<Reward>(`/admin/rewards/${id}`),
      create: (data: RewardCreatePayload): Promise<ApiResponse<Reward>> =>
        apiRequest<Reward>('/admin/rewards', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: RewardUpdatePayload
      ): Promise<ApiResponse<Reward>> =>
        apiRequest<Reward>(`/admin/rewards/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/rewards/${id}`, { method: 'DELETE' }).then(
          r => r
        ),
    },

    bonusPromotions: {
      list: (params?: {
        status?: boolean
        q?: string
      }): Promise<ApiResponse<BonusPromotion[]>> => {
        const searchParams = new URLSearchParams()
        if (params?.status !== undefined) {
          searchParams.set('status', params.status ? '1' : '0')
        }
        if (params?.q) {
          searchParams.set('q', params.q)
        }
        const qs = searchParams.toString()
        const url = qs
          ? `/admin/bonus-promotions?${qs}`
          : '/admin/bonus-promotions'
        return apiRequest<BonusPromotion[]>(url)
      },
      detail: (id: number): Promise<ApiResponse<BonusPromotion>> =>
        apiRequest<BonusPromotion>(`/admin/bonus-promotions/${id}`),
      create: (
        data: BonusPromotionCreatePayload
      ): Promise<ApiResponse<BonusPromotion>> =>
        apiRequest<BonusPromotion>('/admin/bonus-promotions', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: BonusPromotionUpdatePayload
      ): Promise<ApiResponse<BonusPromotion>> =>
        (() => {
          const normalizedId = Number(id)
          if (!Number.isFinite(normalizedId)) {
            throw new Error('Invalid bonus promotion id')
          }
          return apiRequest<BonusPromotion>(
            `/admin/bonus-promotions/${normalizedId}`,
            {
              method: 'PUT',
              body: JSON.stringify(data),
            }
          )
        })(),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/bonus-promotions/${id}`, {
          method: 'DELETE',
        }).then(r => r),
      allocations: (params?: {
        status?: string | null
        page?: number | null
        limit?: number | null
        search?: string | null
      }): Promise<
        ApiResponse<{
          items: BonusJournal[]
          pagination: {
            page: number
            limit: number
            total: number
            totalPages: number
          }
        }>
      > => {
        const searchParams = new URLSearchParams()
        if (params?.status) {
          searchParams.set('status', params.status)
        }
        if (params?.page) {
          searchParams.set('page', String(params.page))
        }
        if (params?.limit) {
          searchParams.set('limit', String(params.limit))
        }
        if (params?.search) {
          searchParams.set('search', params.search)
        }
        const qs = searchParams.toString()
        const url = qs
          ? `/admin/bonus-promotions/allocations?${qs}`
          : '/admin/bonus-promotions/allocations'
        return apiRequest<{
          items: BonusJournal[]
          pagination: {
            page: number
            limit: number
            total: number
            totalPages: number
          }
        }>(url)
      },
      approveAllocation: (
        id: number,
        data: { approved: boolean; notes?: string | null }
      ): Promise<ApiResponse<BonusJournal>> =>
        (() => {
          const normalizedId = Number(id)
          if (!Number.isFinite(normalizedId)) {
            throw new Error('Invalid bonus allocation id')
          }
          return apiRequest<BonusJournal>(
            `/admin/bonus-promotions/allocations/${normalizedId}/approve`,
            {
              method: 'POST',
              body: JSON.stringify(data),
            }
          )
        })(),
      groupKeys: (): Promise<ApiResponse<string[]>> =>
        apiRequest<string[]>('/admin/bonus-promotions/group-keys'),
    },

    activeBonuses: {
      list: (params?: {
        status?: string
        userId?: number
        promotionId?: number
      }): Promise<ApiResponse<ActiveBonusRecord[]>> => {
        const searchParams = new URLSearchParams()
        if (params?.status) {
          searchParams.set('status', params.status)
        }
        if (params?.userId !== undefined) {
          searchParams.set('userId', String(params.userId))
        }
        if (params?.promotionId !== undefined) {
          searchParams.set('promotionId', String(params.promotionId))
        }
        const qs = searchParams.toString()
        const url = qs ? `/admin/active-bonuses?${qs}` : '/admin/active-bonuses'
        return apiRequest<ActiveBonusRecord[]>(url)
      },
      get: (userId: number): Promise<ApiResponse<ActiveBonusRecord | null>> =>
        apiRequest<ActiveBonusRecord | null>(
          `/admin/active-bonuses/user/${userId}`
        ),
      settlementPreview: (
        activeBonusId: number
      ): Promise<ApiResponse<SettlementPreview>> =>
        apiRequest<SettlementPreview>(
          `/admin/active-bonuses/${activeBonusId}/settlement-preview`
        ),
      close: (activeBonusId: number): Promise<ApiResponse<ActiveBonusRecord>> =>
        apiRequest<ActiveBonusRecord>(
          `/admin/active-bonuses/${activeBonusId}/close`,
          {
            method: 'POST',
          }
        ),
      settle: (
        activeBonusId: number
      ): Promise<ApiResponse<SettlementSummary>> =>
        apiRequest<SettlementSummary>(
          `/admin/active-bonuses/${activeBonusId}/settle`,
          {
            method: 'POST',
          }
        ),
      approve: (
        activeBonusId: number
      ): Promise<ApiResponse<ActiveBonusRecord>> =>
        apiRequest<ActiveBonusRecord>(
          `/admin/active-bonuses/${activeBonusId}/approve`,
          {
            method: 'POST',
          }
        ),
      reopen: (
        activeBonusId: number
      ): Promise<ApiResponse<ActiveBonusRecord>> =>
        apiRequest<ActiveBonusRecord>(
          `/admin/active-bonuses/${activeBonusId}/reopen`,
          {
            method: 'POST',
          }
        ),
      rematchCroco: (
        activeBonusId: number
      ): Promise<ApiResponse<{ message: string }>> =>
        apiRequest<{ message: string }>(
          `/admin/active-bonuses/${activeBonusId}/rematch-croco`,
          {
            method: 'POST',
          }
        ),
      syncCroco: (
        activeBonusId: number
      ): Promise<ApiResponse<{ message: string }>> =>
        apiRequest<{ message: string }>(
          `/admin/active-bonuses/${activeBonusId}/sync-croco`,
          {
            method: 'POST',
          }
        ),
    },

    bonusJournals: {
      list: (params?: {
        status?: string | null
        eventType?: string | null
        promotionId?: number
        userId?: number
        page?: number
        limit?: number
        search?: string | null
      }): Promise<ApiResponse<BonusJournalListResponse>> => {
        const searchParams = new URLSearchParams()
        if (params?.status) searchParams.set('status', params.status)
        if (params?.eventType) searchParams.set('eventType', params.eventType)
        if (params?.promotionId !== undefined) {
          searchParams.set('promotionId', String(params.promotionId))
        }
        if (params?.userId !== undefined) {
          searchParams.set('userId', String(params.userId))
        }
        if (params?.page !== undefined)
          searchParams.set('page', String(params.page))
        if (params?.limit !== undefined) {
          searchParams.set('limit', String(params.limit))
        }
        if (params?.search) searchParams.set('search', params.search)
        const qs = searchParams.toString()
        const url = qs ? `/admin/bonus-journals?${qs}` : '/admin/bonus-journals'
        return apiRequest<BonusJournal[]>(url).then(response => {
          const pagination = response.pagination as
            | {
                total?: number
                page?: number
                limit?: number
                totalPages?: number
              }
            | undefined
          const items = Array.isArray(response.data) ? response.data : []
          const limitValue = pagination?.limit ?? params?.limit ?? 20
          const totalCount = pagination?.total ?? items.length
          return {
            ...response,
            data: {
              items,
              total: totalCount,
              page: pagination?.page ?? params?.page ?? 1,
              limit: limitValue,
              totalPages:
                pagination?.totalPages ??
                Math.max(1, Math.ceil(totalCount / Math.max(1, limitValue))),
            },
          }
        })
      },
      detail: (id: number): Promise<ApiResponse<BonusJournal>> =>
        (() => {
          const normalizedId = Number(id)
          if (!Number.isFinite(normalizedId)) {
            throw new Error('Invalid bonus journal id')
          }
          return apiRequest<BonusJournal>(
            `/admin/bonus-journals/${normalizedId}`
          )
        })(),
      create: (
        data: BonusJournalCreatePayload
      ): Promise<ApiResponse<BonusJournal>> =>
        apiRequest<BonusJournal>('/admin/bonus-journals', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: BonusJournalUpdatePayload
      ): Promise<ApiResponse<BonusJournal>> =>
        (() => {
          const normalizedId = Number(id)
          if (!Number.isFinite(normalizedId)) {
            throw new Error('Invalid bonus journal id')
          }
          return apiRequest<BonusJournal>(
            `/admin/bonus-journals/${normalizedId}`,
            {
              method: 'PUT',
              body: JSON.stringify(data),
            }
          )
        })(),
      delete: (id: number): Promise<ApiResponse<void>> =>
        (() => {
          const normalizedId = Number(id)
          if (!Number.isFinite(normalizedId)) {
            throw new Error('Invalid bonus journal id')
          }
          return apiRequest<void>(`/admin/bonus-journals/${normalizedId}`, {
            method: 'DELETE',
          }).then(r => r)
        })(),
    },

    bonusAgents: {
      list: (): Promise<ApiResponse<BonusAgent[]>> =>
        apiRequest<BonusAgent[]>('/admin/bonus-agents'),
      detail: (id: number): Promise<ApiResponse<BonusAgent>> =>
        apiRequest<BonusAgent>(`/admin/bonus-agents/${id}`),
      create: (
        data: BonusAgentCreatePayload
      ): Promise<ApiResponse<BonusAgent>> =>
        apiRequest<BonusAgent>('/admin/bonus-agents', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: BonusAgentUpdatePayload
      ): Promise<ApiResponse<BonusAgent>> =>
        (() => {
          const normalizedId = Number(id)
          if (!Number.isFinite(normalizedId)) {
            throw new Error('Invalid bonus agent id')
          }
          return apiRequest<BonusAgent>(`/admin/bonus-agents/${normalizedId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          })
        })(),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/bonus-agents/${id}`, {
          method: 'DELETE',
        }).then(r => r),
    },

    bonusAgentJournals: {
      list: (params?: {
        status?: string | null
        bonusAgentId?: number
        sourceUserId?: number
        beneficialUserId?: number
        page?: number
        limit?: number
        search?: string | null
      }): Promise<ApiResponse<BonusAgentJournalListResponse>> => {
        const searchParams = new URLSearchParams()
        if (params?.status) searchParams.set('status', params.status)
        if (params?.bonusAgentId !== undefined) {
          searchParams.set('bonusAgentId', String(params.bonusAgentId))
        }
        if (params?.sourceUserId !== undefined) {
          searchParams.set('sourceUserId', String(params.sourceUserId))
        }
        if (params?.beneficialUserId !== undefined) {
          searchParams.set('beneficialUserId', String(params.beneficialUserId))
        }
        if (params?.page !== undefined)
          searchParams.set('page', String(params.page))
        if (params?.limit !== undefined) {
          searchParams.set('limit', String(params.limit))
        }
        if (params?.search) searchParams.set('search', params.search)
        const qs = searchParams.toString()
        const url = qs
          ? `/admin/bonus-agent-journals?${qs}`
          : '/admin/bonus-agent-journals'
        return apiRequest<BonusAgentJournal[]>(url).then(response => {
          const pagination = response.pagination as
            | {
                total?: number
                page?: number
                limit?: number
                totalPages?: number
              }
            | undefined
          const items = Array.isArray(response.data) ? response.data : []
          const limitValue = pagination?.limit ?? params?.limit ?? 20
          const totalCount = pagination?.total ?? items.length
          return {
            ...response,
            data: {
              items,
              total: totalCount,
              page: pagination?.page ?? params?.page ?? 1,
              limit: limitValue,
              totalPages:
                pagination?.totalPages ??
                Math.max(1, Math.ceil(totalCount / Math.max(1, limitValue))),
            },
          }
        })
      },
      detail: (id: number): Promise<ApiResponse<BonusAgentJournal>> =>
        apiRequest<BonusAgentJournal>(`/admin/bonus-agent-journals/${id}`),
    },

    crocoTransactions: {
      list: (params?: CrocoTransactionFilters): Promise<ApiResponse<any>> => {
        const searchParams = new URLSearchParams()
        if (
          params?.userWalletId !== undefined &&
          params?.userWalletId !== null
        ) {
          searchParams.set('userWalletId', String(params.userWalletId))
        }
        if (
          params?.walletJournalId !== undefined &&
          params?.walletJournalId !== null
        ) {
          searchParams.set('walletJournalId', String(params.walletJournalId))
        }
        if (params?.userId !== undefined && params?.userId !== null)
          searchParams.set('userId', String(params.userId))
        if (params?.gameId !== undefined && params?.gameId !== null)
          searchParams.set('gameId', String(params.gameId))
        if (params?.page !== undefined && params?.page !== null)
          searchParams.set('page', String(params.page))
        if (params?.limit !== undefined && params?.limit !== null)
          searchParams.set('limit', String(params.limit))
        if (params?.search) searchParams.set('search', params.search)
        if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom)
        if (params?.dateTo) searchParams.set('dateTo', params.dateTo)
        if (params?.result) searchParams.set('result', params.result)
        if (params?.minBet !== undefined && params?.minBet !== null)
          searchParams.set('minBet', String(params.minBet))
        if (params?.maxBet !== undefined && params?.maxBet !== null)
          searchParams.set('maxBet', String(params.maxBet))
        const qs = searchParams.toString()
        const url = qs
          ? `/admin/croco-transactions?${qs}`
          : '/admin/croco-transactions'
        return apiRequest(url).then(response => {
          const pagination = response.pagination as
            | {
                total?: number
                page?: number
                limit?: number
                totalPages?: number
              }
            | undefined
          const items = Array.isArray(response.data) ? response.data : []
          const limitValue = pagination?.limit ?? params?.limit ?? 20
          const totalCount = pagination?.total ?? items.length
          return {
            ...response,
            data: {
              items,
              total: totalCount,
              page: pagination?.page ?? params?.page ?? 1,
              limit: limitValue,
              totalPages:
                pagination?.totalPages ??
                Math.max(1, Math.ceil(totalCount / Math.max(1, limitValue))),
            },
          }
        })
      },
    },

    walletJournals: {
      list: (params?: {
        walletId?: number
        userId?: number
        page?: number
        limit?: number
      }): Promise<ApiResponse<WalletJournalListResponse>> => {
        const searchParams = new URLSearchParams()
        if (params?.walletId !== undefined) {
          searchParams.set('walletId', String(params.walletId))
        }
        if (params?.userId !== undefined) {
          searchParams.set('userId', String(params.userId))
        }
        if (params?.page !== undefined) {
          searchParams.set('page', String(params.page))
        }
        if (params?.limit !== undefined) {
          searchParams.set('limit', String(params.limit))
        }
        const qs = searchParams.toString()
        const url = qs
          ? `/admin/wallet-journals?${qs}`
          : '/admin/wallet-journals'
        return apiRequest<WalletJournal[]>(url).then(response => {
          const pagination = response.pagination as
            | {
                page: number
                limit: number
                total: number
                totalPages?: number
              }
            | undefined
          const items = Array.isArray(response.data) ? response.data : []
          const limitValue = pagination?.limit ?? params?.limit ?? 20
          const totalCount = pagination?.total ?? items.length
          return {
            ...response,
            data: {
              items,
              total: totalCount,
              page: pagination?.page ?? params?.page ?? 1,
              limit: limitValue,
              totalPages:
                pagination?.totalPages ??
                Math.max(1, Math.ceil(totalCount / Math.max(1, limitValue))),
            },
          }
        })
      },
      detail: (id: number): Promise<ApiResponse<WalletJournal>> =>
        apiRequest<WalletJournal>(`/admin/wallet-journals/${id}`),
      create: (
        data: WalletJournalCreatePayload
      ): Promise<ApiResponse<WalletJournal>> =>
        apiRequest<WalletJournal>('/admin/wallet-journals', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: WalletJournalUpdatePayload
      ): Promise<ApiResponse<WalletJournal>> =>
        apiRequest<WalletJournal>(`/admin/wallet-journals/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/wallet-journals/${id}`, {
          method: 'DELETE',
        }).then(r => r),
    },

    payouts: {
      list: async (
        params?: PayoutListParams
      ): Promise<ApiResponse<PayoutListResponse>> => {
        const searchParams = new URLSearchParams()
        if (params?.status) {
          searchParams.set('status', params.status)
        }
        if (params?.page !== undefined) {
          searchParams.set('page', String(params.page))
        }
        if (params?.limit !== undefined) {
          searchParams.set('limit', String(params.limit))
        }
        const qs = searchParams.toString()
        const url = qs ? `/admin/payouts?${qs}` : '/admin/payouts'
        const response = await apiRequest<Payout[]>(url)
        // Transform server response to expected format
        // apiRequest extracts data.data, so response.data is the array
        // pagination is at response.pagination
        const pagination = response.pagination as
          | { page: number; limit: number; total: number }
          | undefined
        return {
          ...response,
          data: {
            items: Array.isArray(response.data) ? response.data : [],
            total: pagination?.total ?? 0,
            page: pagination?.page ?? 1,
            limit: pagination?.limit ?? 20,
          },
        }
      },
      detail: (id: number): Promise<ApiResponse<Payout>> =>
        apiRequest<Payout>(`/admin/payouts/${id}`),
      status: (
        id: number
      ): Promise<
        ApiResponse<{
          id: number
          status: PayoutStatus
          statusId: string
          completedAt: string | null
          updatedAt: string
        }>
      > => apiRequest(`/admin/payouts/${id}/status`),
      create: (data: PayoutCreatePayload): Promise<ApiResponse<Payout>> =>
        apiRequest<Payout>('/admin/payouts', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: PayoutUpdatePayload
      ): Promise<ApiResponse<Payout>> =>
        apiRequest<Payout>(`/admin/payouts/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      balance: (
        currency?: string
      ): Promise<
        ApiResponse<{ balance: string; currency: string; merchant: string }>
      > => {
        const params = currency ? `?currency=${currency}` : ''
        return apiRequest<{
          balance: string
          currency: string
          merchant: string
        }>(`/admin/payouts/balance${params}`)
      },
    },

    llm: {
      listProviders: (): Promise<ApiResponse<LlmProviderPricing[]>> =>
        apiRequest<LlmProviderPricing[]>(`/admin/llm/providers`),
      createProvider: (
        data: LlmPricingRequest
      ): Promise<ApiResponse<LlmProviderPricing>> =>
        apiRequest<LlmProviderPricing>('/admin/llm/providers', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      updateProvider: (
        id: number,
        data: LlmPricingRequest
      ): Promise<ApiResponse<LlmProviderPricing>> =>
        apiRequest<LlmProviderPricing>(`/admin/llm/providers/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      deleteProvider: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/llm/providers/${id}`, {
          method: 'DELETE',
        }).then(r => r),
      listUsage: (params?: {
        providerCode?: string
        limit?: number
        page?: number
        pageSize?: number
        start?: string
        end?: string
      }): Promise<ApiResponse<LlmUsageListResponse>> => {
        const searchParams = new URLSearchParams()

        if (params?.providerCode) {
          searchParams.set('providerCode', params.providerCode)
        }

        if (
          params?.limit !== undefined &&
          Number.isFinite(params.limit) &&
          params.limit > 0
        ) {
          searchParams.set('limit', String(Math.trunc(params.limit)))
        }

        if (
          params?.page !== undefined &&
          Number.isFinite(params.page) &&
          params.page > 0
        ) {
          searchParams.set('page', String(Math.trunc(params.page)))
        }

        if (
          params?.pageSize !== undefined &&
          Number.isFinite(params.pageSize) &&
          params.pageSize > 0
        ) {
          searchParams.set('pageSize', String(Math.trunc(params.pageSize)))
        }

        if (params?.start) {
          searchParams.set('start', params.start)
        }

        if (params?.end) {
          searchParams.set('end', params.end)
        }

        const suffix = searchParams.toString()
        const endpoint = suffix
          ? `/admin/llm/usage?${suffix}`
          : '/admin/llm/usage'

        return apiRequest<LlmUsageListResponse>(endpoint)
      },
    },

    // Agent Management
    listAgents: (): Promise<ApiResponse<AgentListItem[]>> => {
      return apiRequest<AgentListItem[]>(`/admin/agents`)
    },
    getAgentById: (id: number): Promise<ApiResponse<AgentListItem>> =>
      apiRequest<AgentListItem>(`/admin/agents/${id}`),
    createAgent: (
      data: AgentCreatePayload
    ): Promise<ApiResponse<AgentListItem>> =>
      apiRequest<AgentListItem>('/admin/agents', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateAgent: (
      id: number,
      data: AgentUpdatePayload
    ): Promise<ApiResponse<AgentListItem>> =>
      apiRequest<AgentListItem>(`/admin/agents/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteAgent: (id: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/agents/${id}`, { method: 'DELETE' }).then(
        r => r
      ),

    // Agent Statistics
    getAgentStats: (): Promise<
      ApiResponse<{
        totalAgents: number
        onlineAgents: number
        activeSessions: number
        pendingHandovers: number
      }>
    > =>
      apiRequest<{
        totalAgents: number
        onlineAgents: number
        activeSessions: number
        pendingHandovers: number
      }>('/admin/agents/stats'),

    // Bot Management
    listBots: (): Promise<ApiResponse<Bot[]>> => {
      return apiRequest<Bot[]>(`/admin/bots`)
    },
    getBotById: (id: number): Promise<ApiResponse<Bot>> =>
      apiRequest<Bot>(`/admin/bots/${id}`),
    createBot: (data: BotCreationRequest): Promise<ApiResponse<Bot>> =>
      apiRequest<Bot>('/admin/bots', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateBot: (
      id: number,
      data: BotUpdateRequest
    ): Promise<ApiResponse<Bot>> =>
      apiRequest<Bot>(`/admin/bots/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteBot: (id: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/bots/${id}`, { method: 'DELETE' }).then(r => r),
    aiTools: {
      list: (): Promise<ApiResponse<AiTool[]>> =>
        apiRequest<AiTool[]>(`/admin/ai-tools`),
      getById: (id: number): Promise<ApiResponse<AiTool>> =>
        apiRequest<AiTool>(`/admin/ai-tools/${id}`),
      create: (data: AiToolPayload): Promise<ApiResponse<AiTool>> =>
        apiRequest<AiTool>(`/admin/ai-tools`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: AiToolUpdatePayload
      ): Promise<ApiResponse<AiTool>> =>
        apiRequest<AiTool>(`/admin/ai-tools/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/ai-tools/${id}`, { method: 'DELETE' }).then(
          r => r
        ),
      resetErpnextToolParameters: (
        id: number
      ): Promise<ApiResponse<{ message: string }>> =>
        apiRequest<{ message: string }>(
          `/admin/ai-tools/${id}/reset-parameters`,
          {
            method: 'POST',
          }
        ),
      getBuiltInTypes: (): Promise<
        ApiResponse<Array<{ value: string; label: string }>>
      > =>
        apiRequest<Array<{ value: string; label: string }>>(
          `/admin/ai-tools/builtin-types`
        ),
    },

    elevenLabs: {
      listConfigs: (): Promise<ApiResponse<ElevenLabsConfig[]>> =>
        apiRequest<ElevenLabsConfig[]>(`/admin/elevenlabs/configs`),
      getConfig: (id: number): Promise<ApiResponse<ElevenLabsConfig>> =>
        apiRequest<ElevenLabsConfig>(`/admin/elevenlabs/configs/${id}`),
      createConfig: (
        data: ElevenLabsConfigPayload
      ): Promise<ApiResponse<ElevenLabsConfig>> =>
        apiRequest<ElevenLabsConfig>(`/admin/elevenlabs/configs`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      updateConfig: (
        id: number,
        data: ElevenLabsConfigUpdatePayload
      ): Promise<ApiResponse<ElevenLabsConfig>> =>
        apiRequest<ElevenLabsConfig>(`/admin/elevenlabs/configs/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      deleteConfig: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/elevenlabs/configs/${id}`, {
          method: 'DELETE',
        }).then(r => r),
      activateConfig: (id: number): Promise<ApiResponse<ElevenLabsConfig>> =>
        apiRequest<ElevenLabsConfig>(
          `/admin/elevenlabs/configs/${id}/activate`,
          {
            method: 'POST',
          }
        ),
      pushFlow: (
        payload: ElevenLabsPushRequest
      ): Promise<ApiResponse<ElevenLabsPushResponse>> =>
        apiRequest<ElevenLabsPushResponse>(`/admin/elevenlabs/flows/push`, {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
    },
    voiceRealtime: {
      listConfigs: (): Promise<ApiResponse<VoiceRealtimeConfig[]>> =>
        apiRequest<VoiceRealtimeConfig[]>(`/admin/voice-realtime/configs`),
      getConfig: (id: number): Promise<ApiResponse<VoiceRealtimeConfig>> =>
        apiRequest<VoiceRealtimeConfig>(`/admin/voice-realtime/configs/${id}`),
      createConfig: (
        data: VoiceRealtimeConfigPayload
      ): Promise<ApiResponse<VoiceRealtimeConfig>> =>
        apiRequest<VoiceRealtimeConfig>(`/admin/voice-realtime/configs`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      updateConfig: (
        id: number,
        data: VoiceRealtimeConfigUpdatePayload
      ): Promise<ApiResponse<VoiceRealtimeConfig>> =>
        apiRequest<VoiceRealtimeConfig>(`/admin/voice-realtime/configs/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      deleteConfig: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/voice-realtime/configs/${id}`, {
          method: 'DELETE',
        }).then(r => r),
      activateConfig: (id: number): Promise<ApiResponse<VoiceRealtimeConfig>> =>
        apiRequest<VoiceRealtimeConfig>(
          `/admin/voice-realtime/configs/${id}/activate`,
          {
            method: 'POST',
          }
        ),
    },

    embedConfigs: {
      list: (flowId?: number): Promise<ApiResponse<EmbedConfiguration[]>> =>
        apiRequest<EmbedConfiguration[]>(
          `/admin/embed-configs/configs${flowId ? `?flowId=${flowId}` : ''}`
        ),
      get: (id: number): Promise<ApiResponse<EmbedConfiguration>> =>
        apiRequest<EmbedConfiguration>(`/admin/embed-configs/configs/${id}`),
      create: (
        data: EmbedConfigPayload
      ): Promise<ApiResponse<EmbedConfiguration>> =>
        apiRequest<EmbedConfiguration>(`/admin/embed-configs/configs`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: EmbedConfigUpdatePayload
      ): Promise<ApiResponse<EmbedConfiguration>> =>
        apiRequest<EmbedConfiguration>(`/admin/embed-configs/configs/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/embed-configs/configs/${id}`, {
          method: 'DELETE',
        }),
    },

    // Team Management
    listTeams: (): Promise<ApiResponse<Team[]>> => {
      return apiRequest<Team[]>(`/admin/teams`)
    },
    getTeamById: (id: number): Promise<ApiResponse<Team>> =>
      apiRequest<Team>(`/admin/teams/${id}`),
    createTeam: (data: TeamCreatePayload): Promise<ApiResponse<Team>> =>
      apiRequest<Team>('/admin/teams', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateTeam: (
      id: number,
      data: TeamUpdatePayload
    ): Promise<ApiResponse<Team>> =>
      apiRequest<Team>(`/admin/teams/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    recalculateTeamConversationsPerformance: (
      id: number,
      payload: TeamReportRecalcPayload
    ): Promise<ApiResponse<TeamReportRecalcResponse>> =>
      apiRequest<TeamReportRecalcResponse>(
        `/admin/teams/${id}/reports/conversations/recalculate`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ),
    deleteTeam: (id: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/teams/${id}`, { method: 'DELETE' }).then(r => r),

    // Team Assignment
    setUserTeams: (
      userId: number,
      teamIds: number[]
    ): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ teamIds }),
      }),
    assignBotToTeam: (
      botId: number,
      teamId: number
    ): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/bots/${botId}/team`, {
        method: 'PUT',
        body: JSON.stringify({ teamId }),
      }),
    removeBotFromTeam: (botId: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/bots/${botId}/team`, {
        method: 'DELETE',
      }),

    // Flow Management APIs
    flows: {
      // Get all flows (optionally scoped to a site) with status filtering
      getAllFlows: (
        includeInactive = false
      ): Promise<ApiResponse<AdminFlowSummary[]>> => {
        const url = `/admin/flows?includeInactive=${includeInactive}`
        return apiRequest<AdminFlowSummary[]>(url)
      },

      // Get flow by ID
      getFlow: (id: number): Promise<ApiResponse<AdminFlowWithDetails>> =>
        apiRequest<AdminFlowWithDetails>(`/admin/flows/${id}`),

      // Create flow
      createFlow: (
        data: FlowUpsertPayload
      ): Promise<ApiResponse<AdminFlowSummary>> =>
        apiRequest<AdminFlowSummary>('/admin/flows', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      // Update flow
      updateFlow: (
        id: number,
        data: FlowUpsertPayload
      ): Promise<ApiResponse<AdminFlowSummary>> =>
        apiRequest<AdminFlowSummary>(`/admin/flows/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      // Delete flow
      deleteFlow: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/flows/${id}`, { method: 'DELETE' }).then(
          r => r
        ),

      // Set flow status
      setFlowStatus: (
        id: number,
        isActive: boolean
      ): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/flows/${id}/status`, {
          method: 'PUT',
          body: JSON.stringify({ isActive }),
        }),

      // Flow module availability
      getModules: (): Promise<ApiResponse<FlowModuleToggle[]>> =>
        apiRequest<FlowModuleToggle[]>('/admin/flows/modules'),

      updateModules: (
        modules: Array<{ nodeType: FlowNodeModuleType; enabled: boolean }>
      ): Promise<ApiResponse<FlowModuleToggle[]>> =>
        apiRequest<FlowModuleToggle[]>('/admin/flows/modules', {
          method: 'PUT',
          body: JSON.stringify({ modules }),
        }),

      // Clone flow
      cloneFlow: (
        id: number,
        data?: FlowClonePayload
      ): Promise<ApiResponse<AdminFlowSummary>> =>
        apiRequest<AdminFlowSummary>(`/admin/flows/${id}/clone`, {
          method: 'POST',
          body: data ? JSON.stringify(data) : undefined,
        }),

      // Validate flow
      validateFlow: (
        id: number
      ): Promise<
        ApiResponse<{ isValid: boolean; errors: string[]; warnings: string[] }>
      > =>
        apiRequest<{ isValid: boolean; errors: string[]; warnings: string[] }>(
          `/admin/flows/${id}/validate`,
          {
            method: 'POST',
          }
        ),

      // Test flow
      testFlow: (
        id: number
      ): Promise<ApiResponse<{ success: boolean; message: string }>> =>
        apiRequest<{ success: boolean; message: string }>(
          `/admin/flows/${id}/test`,
          {
            method: 'POST',
          }
        ),

      translateField: (payload: {
        text: string
        prompt?: string
        model?: string
      }): Promise<ApiResponse<{ translatedText: string; model?: string }>> =>
        apiRequest<{ translatedText: string; model?: string }>(
          '/admin/flows/translate',
          {
            method: 'POST',
            body: JSON.stringify(payload),
          }
        ),

      // Node operations
      getNodes: (flowId: number): Promise<ApiResponse<AdminFlowNode[]>> =>
        apiRequest<AdminFlowNode[]>(`/admin/flows/${flowId}/nodes`),

      getNodeSaveCounts: (
        flowId: number
      ): Promise<ApiResponse<Array<{ id: string; saveCount: number }>>> =>
        apiRequest<Array<{ id: string; saveCount: number }>>(
          `/admin/flows/${flowId}/nodes/save-counts`
        ),

      createNode: (
        flowId: number,
        node: FlowNodeUpsertPayload
      ): Promise<ApiResponse<AdminFlowNode>> =>
        apiRequest<AdminFlowNode>(`/admin/flows/${flowId}/nodes`, {
          method: 'POST',
          body: JSON.stringify(node),
        }),

      updateNode: (
        flowId: number,
        nodeId: string,
        data: FlowNodeUpsertPayload
      ): Promise<ApiResponse<AdminFlowNode>> =>
        apiRequest<AdminFlowNode>(`/admin/flows/${flowId}/nodes/${nodeId}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      deleteNode: (
        flowId: number,
        nodeId: string
      ): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/flows/${flowId}/nodes/${nodeId}`, {
          method: 'DELETE',
        }),

      batchSaveNodes: (
        flowId: number,
        payload: {
          nodesToCreate?: FlowNodeUpsertPayload[]
          nodesToUpdate?: Array<{ id: string; data: FlowNodeUpsertPayload }>
          nodesToDelete?: string[]
        }
      ): Promise<
        ApiResponse<{
          created: AdminFlowNode[]
          updated: AdminFlowNode[]
          deleted: string[]
        }>
      > =>
        apiRequest<{
          created: AdminFlowNode[]
          updated: AdminFlowNode[]
          deleted: string[]
        }>(`/admin/flows/${flowId}/nodes/batch`, {
          method: 'POST',
          body: JSON.stringify(payload),
        }),

      // Connection operations
      getConnections: (
        flowId: number
      ): Promise<ApiResponse<AdminFlowConnection[]>> =>
        apiRequest<AdminFlowConnection[]>(
          `/admin/flows/${flowId}/connections`
        ).then(r => r),

      createConnection: (
        flowId: number,
        connection: FlowConnectionUpsertPayload
      ): Promise<ApiResponse<AdminFlowConnection>> =>
        apiRequest<AdminFlowConnection>(`/admin/flows/${flowId}/connections`, {
          method: 'POST',
          body: JSON.stringify(connection),
        }),

      updateConnection: (
        flowId: number,
        connectionId: string,
        data: FlowConnectionUpsertPayload
      ): Promise<ApiResponse<AdminFlowConnection>> =>
        apiRequest<AdminFlowConnection>(
          `/admin/flows/${flowId}/connections/${connectionId}`,
          {
            method: 'PUT',
            body: JSON.stringify(data),
          }
        ),

      deleteConnection: (
        flowId: number,
        connectionId: string
      ): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/flows/${flowId}/connections/${connectionId}`, {
          method: 'DELETE',
        }),

      // Lock operations
      getFlowLocks: (flowId: number): Promise<ApiResponse<any[]>> =>
        apiRequest<any[]>(`/admin/flows/${flowId}/locks`),

      acquireNodeLock: (
        flowId: number,
        nodeId: string
      ): Promise<ApiResponse<{ lock: any }>> =>
        apiRequest<{ lock: any }>(
          `/admin/flows/${flowId}/nodes/${nodeId}/lock`,
          {
            method: 'POST',
            body: '{}',
          }
        ),

      releaseNodeLock: (
        flowId: number,
        nodeId: string
      ): Promise<ApiResponse<{ released: boolean }>> =>
        apiRequest<{ released: boolean }>(
          `/admin/flows/${flowId}/nodes/${nodeId}/lock`,
          { method: 'DELETE' }
        ),

      releaseAllLocks: (
        flowId: number
      ): Promise<ApiResponse<{ releasedCount: number }>> =>
        apiRequest<{ releasedCount: number }>(`/admin/flows/${flowId}/locks`, {
          method: 'DELETE',
        }),

      heartbeatLocks: (
        flowId: number
      ): Promise<ApiResponse<{ locksExtended: number }>> =>
        apiRequest<{ locksExtended: number }>(
          `/admin/flows/${flowId}/locks/heartbeat`,
          { method: 'POST', body: '{}' }
        ),

      getLatestNode: (
        flowId: number,
        nodeId: string
      ): Promise<ApiResponse<AdminFlowNode>> =>
        apiRequest<AdminFlowNode>(
          `/admin/flows/${flowId}/nodes/${nodeId}/latest`
        ),

      batchSaveConnections: (
        flowId: number,
        payload: {
          connectionsToCreate?: FlowConnectionUpsertPayload[]
          connectionsToUpdate?: Array<{
            id: string
            data: {
              sourceNodeId?: string
              targetNodeId?: string
              sourceHandle?: string | null
              condition?: JsonObject
            }
          }>
          connectionsToDelete?: string[]
        }
      ): Promise<
        ApiResponse<{
          created: AdminFlowConnection[]
          updated: AdminFlowConnection[]
          deleted: string[]
        }>
      > =>
        apiRequest<{
          created: AdminFlowConnection[]
          updated: AdminFlowConnection[]
          deleted: string[]
        }>(`/admin/flows/${flowId}/connections/batch`, {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
    },

    // AI Process Orchestrator APIs
    aiProcesses: {
      getAllProcesses: (): Promise<ApiResponse<AIProcess[]>> =>
        apiRequest<AIProcess[]>(`/admin/ai-processes`),

      getProcess: (id: number): Promise<ApiResponse<AIProcess>> =>
        apiRequest<AIProcess>(`/admin/ai-processes/${id}`),

      createProcess: (
        data: AIProcessPayload
      ): Promise<ApiResponse<AIProcess>> =>
        apiRequest<AIProcess>('/admin/ai-processes', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      updateProcess: (
        id: number,
        data: AIProcessPayload
      ): Promise<ApiResponse<AIProcess>> =>
        apiRequest<AIProcess>(`/admin/ai-processes/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      deleteProcess: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/ai-processes/${id}`, {
          method: 'DELETE',
        }).then(r => r),

      cloneProcess: (
        id: number,
        data?: AIProcessClonePayload
      ): Promise<ApiResponse<AIProcess>> =>
        apiRequest<AIProcess>(`/admin/ai-processes/${id}/clone`, {
          method: 'POST',
          body: data ? JSON.stringify(data) : undefined,
        }),

      testProcess: (id: number): Promise<ApiResponse<AIProcessTestResponse>> =>
        apiRequest<AIProcessTestResponse>(`/admin/ai-processes/${id}/test`, {
          method: 'POST',
        }),

      getProcessExecutions: (
        id: number,
        params: { limit?: number } = {}
      ): Promise<ApiResponse<AIProcessExecution[]>> => {
        const searchParams = new URLSearchParams()
        if (params.limit) {
          searchParams.set('limit', params.limit.toString())
        }
        const query = searchParams.toString()
        const url = `/admin/ai-processes/${id}/executions${query ? `?${query}` : ''}`
        return apiRequest<AIProcessExecution[]>(url)
      },

      getProcessExecution: (
        id: number,
        executionId: string
      ): Promise<ApiResponse<AIProcessExecution>> =>
        apiRequest<AIProcessExecution>(
          `/admin/ai-processes/${id}/executions/${executionId}`
        ),
    },

    // Telegram Configuration Management APIs
    telegram: {
      // Get all Telegram configurations
      list: (): Promise<ApiResponse<TelegramConfiguration[]>> =>
        apiRequest<TelegramConfiguration[]>('/admin/telegram/list'),

      // Update Telegram webhook (forces Telegram to refresh webhook URL)
      updateWebhook: (
        configId: number,
        webhookUrl: string
      ): Promise<
        ApiResponse<{ success: boolean; webhookUrl?: string; message?: string }>
      > =>
        apiRequest<{ success: boolean; webhookUrl?: string; message?: string }>(
          `/admin/telegram/config/${configId}/webhook`,
          {
            method: 'PUT',
            body: JSON.stringify({ webhookUrl }),
          }
        ),

      // Test Telegram connection
      test: (): Promise<ApiResponse<TelegramTestResponse>> =>
        apiRequest<TelegramTestResponse>('/admin/telegram/test', {
          method: 'POST',
        }),

      testWebhook: (
        configId: number
      ): Promise<ApiResponse<TelegramWebhookTestResponse>> =>
        apiRequest<TelegramWebhookTestResponse>(
          `/admin/telegram/config/${configId}/test-webhook`,
          {
            method: 'POST',
          }
        ),

      // Delete Telegram configuration
      deleteConfig: (configId: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/telegram/config/${configId}`, {
          method: 'DELETE',
        }),

      // Activate Telegram configuration
      activateConfig: (configId: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/telegram/config/${configId}/activate`, {
          method: 'POST',
        }),

      // Deactivate Telegram configuration
      deactivateConfig: (configId: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/telegram/config/${configId}/deactivate`, {
          method: 'POST',
        }),

      // Resync Telegram sessions to the current bot/flow defaults
      resyncConfig: (
        configId: number
      ): Promise<ApiResponse<{ success: boolean; updatedSessions: number }>> =>
        apiRequest<{ success: boolean; updatedSessions: number }>(
          `/admin/telegram/config/${configId}/resync`,
          {
            method: 'POST',
          }
        ),
    },

    // Twilio Configuration Management APIs
    twilio: {
      // Get all Twilio configurations
      list: (): Promise<ApiResponse<any[]>> =>
        apiRequest<any[]>('/admin/twilio/list'),

      // Get specific Twilio configuration
      get: (id: number): Promise<ApiResponse<any>> =>
        apiRequest<any>(`/admin/twilio/${id}`),

      // Create Twilio configuration
      create: (payload: any): Promise<ApiResponse<any>> =>
        apiRequest<any>('/admin/twilio', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),

      // Update Twilio configuration
      update: (id: number, payload: any): Promise<ApiResponse<any>> =>
        apiRequest<any>(`/admin/twilio/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        }),

      // Get webhook URL for Twilio configuration
      getWebhookUrl: (): Promise<ApiResponse<any>> =>
        apiRequest<any>('/admin/twilio/webhook-url'),

      // Delete Twilio configuration
      delete: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/twilio/${id}`, {
          method: 'DELETE',
        }),

      // Activate Twilio configuration
      activate: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/twilio/config/${id}/activate`, {
          method: 'POST',
        }),

      // Deactivate Twilio configuration
      deactivate: (id: number): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/twilio/config/${id}/deactivate`, {
          method: 'POST',
        }),
    },

    // Content Management APIs
    content: {
      // Get all content by section type with pagination and search
      getAllContent: (
        sectionType: string,
        params: { page?: number; limit?: number; search?: string } = {}
      ): Promise<ApiResponse<JsonValue>> => {
        const searchParams = new URLSearchParams()
        if (params.page) searchParams.append('page', params.page.toString())
        if (params.limit) searchParams.append('limit', params.limit.toString())
        if (params.search) searchParams.append('search', params.search)

        const queryString = searchParams.toString()
        const url = `/admin/content/${sectionType}${queryString ? `?${queryString}` : ''}`
        return apiRequest<JsonValue>(url)
      },

      // Get content by ID
      getContentById: (
        sectionType: string,
        id: number
      ): Promise<ApiResponse<JsonValue>> =>
        apiRequest<JsonValue>(`/admin/content/${sectionType}/${id}`).then(
          r => r
        ),

      // Create new content
      createContent: (
        sectionType: string,
        data: JsonObject
      ): Promise<ApiResponse<JsonValue>> =>
        apiRequest<JsonValue>(`/admin/content/${sectionType}`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      // Update content
      updateContent: (
        sectionType: string,
        id: number,
        data: JsonObject
      ): Promise<ApiResponse<JsonValue>> =>
        apiRequest<JsonValue>(`/admin/content/${sectionType}/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      // Delete content
      deleteContent: (
        sectionType: string,
        id: number
      ): Promise<ApiResponse<void>> =>
        apiRequest<void>(`/admin/content/${sectionType}/${id}`, {
          method: 'DELETE',
        }),

      // Reports APIs
      reports: {
        transactions: {
          list: (params?: {
            dateFrom?: string
            dateTo?: string
            status?: string
            provider?: string
            page?: number
            pageSize?: number
          }): Promise<
            ApiResponse<{
              items: Array<{
                id: number
                amount: number
                currency: string
                status: string
                provider: string
                createdAt: string
                updatedAt: string
                userId: number
                userName?: string
                userEmail?: string
              }>
              pagination: {
                page: number
                pageSize: number
                total: number
                totalPages: number
              }
            }>
          > => {
            const searchParams = new URLSearchParams()
            if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom)
            if (params?.dateTo) searchParams.set('dateTo', params.dateTo)
            if (params?.status) searchParams.set('status', params.status)
            if (params?.provider) searchParams.set('provider', params.provider)
            if (params?.page) searchParams.set('page', String(params.page))
            if (params?.pageSize)
              searchParams.set('pageSize', String(params.pageSize))
            const qs = searchParams.toString()
            const url = qs
              ? `/admin/reports/transactions?${qs}`
              : '/admin/reports/transactions'
            return apiRequest(url)
          },

          analytics: (params?: {
            dateFrom?: string
            dateTo?: string
          }): Promise<
            ApiResponse<{
              metrics: {
                totalVolume: number
                totalTransactions: number
                averageTransactionValue: number
                completedTransactions: number
                failedTransactions: number
                successRate: number
              }
              dailyTrends: Array<{
                date: string
                totalAmount: number
                transactionCount: number
                avgAmount: number
              }>
              statusBreakdown: Array<{
                status: string
                count: number
                totalAmount: number
              }>
              providerBreakdown: Array<{
                provider: string
                count: number
                totalAmount: number
              }>
            }>
          > => {
            const searchParams = new URLSearchParams()
            if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom)
            if (params?.dateTo) searchParams.set('dateTo', params.dateTo)
            const qs = searchParams.toString()
            const url = qs
              ? `/admin/reports/transactions/analytics?${qs}`
              : '/admin/reports/transactions/analytics'
            return apiRequest(url)
          },
        },

        walletJournals: {
          list: (params?: {
            walletId?: number
            userId?: number
            dateFrom?: string
            dateTo?: string
            type?: string
            page?: number
            pageSize?: number
          }): Promise<
            ApiResponse<{
              items: Array<{
                id: number
                amount: number
                type: 'credit' | 'debit'
                balance: number
                runningBalance: number
                createdAt: string
                userId: number
                walletId: number
                userName?: string
                userEmail?: string
                walletName?: string
                description?: string
              }>
              pagination: {
                page: number
                pageSize: number
                total: number
                totalPages: number
              }
            }>
          > => {
            const searchParams = new URLSearchParams()
            if (params?.walletId)
              searchParams.set('walletId', String(params.walletId))
            if (params?.userId)
              searchParams.set('userId', String(params.userId))
            if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom)
            if (params?.dateTo) searchParams.set('dateTo', params.dateTo)
            if (params?.type) searchParams.set('type', params.type)
            if (params?.page) searchParams.set('page', String(params.page))
            if (params?.pageSize)
              searchParams.set('pageSize', String(params.pageSize))
            const qs = searchParams.toString()
            const url = qs
              ? `/admin/reports/wallet-journals?${qs}`
              : '/admin/reports/wallet-journals'
            return apiRequest(url)
          },

          summary: (params?: {
            walletId?: number
            dateFrom?: string
            dateTo?: string
          }): Promise<
            ApiResponse<{
              summary: {
                totalCredits: number
                totalDebits: number
                creditCount: number
                debitCount: number
                netBalance: number
              }
              typeBreakdown: Array<{
                type: string
                count: number
                totalAmount: number
              }>
              walletBalances: Array<{
                walletId: number
                walletName?: string
                currentBalance: number
                userId: number
                userName?: string
              }>
              dailyTrends: Array<{
                date: string
                creditTotal: number
                debitTotal: number
                transactionCount: number
              }>
            }>
          > => {
            const searchParams = new URLSearchParams()
            if (params?.walletId)
              searchParams.set('walletId', String(params.walletId))
            if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom)
            if (params?.dateTo) searchParams.set('dateTo', params.dateTo)
            const qs = searchParams.toString()
            const url = qs
              ? `/admin/reports/wallet-journals/summary?${qs}`
              : '/admin/reports/wallet-journals/summary'
            return apiRequest(url)
          },
        },
      },
    },
  },

  // Session Management APIs
  sessions: {
    // Get session statistics (with optional team filtering)
    getStats: (
      teamId?: number
    ): Promise<
      ApiResponse<{
        totalSessions: number
        activeSessions: number
        pendingHandovers: number
        completedToday: number
      }>
    > => {
      const client = createSessionsClient()
      return client.stats
        .$get(
          teamId
            ? {
                query: {
                  teamId: teamId.toString(),
                },
              }
            : undefined
        )
        .then(res =>
          handleHonoResponse(res, payload => payload?.data ?? payload)
        )
    },

    // Get pending handover requests (with optional team filtering)
    getPendingHandovers: (
      teamId?: number
    ): Promise<ApiResponse<HandoverRequest[]>> => {
      const client = createSessionsClient()
      return client.handovers.pending
        .$get(
          teamId
            ? {
                query: {
                  teamId: teamId.toString(),
                },
              }
            : undefined
        )
        .then(res => handleHonoResponse(res, payload => payload?.data ?? []))
    },

    // Get active sessions (with optional team filtering)
    getActiveSessions: (
      teamId?: number
    ): Promise<ApiResponse<HandoverRequest[]>> => {
      const client = createSessionsClient()
      return client.active
        .$get(
          teamId
            ? {
                query: {
                  teamId: teamId.toString(),
                },
              }
            : undefined
        )
        .then(res =>
          handleHonoResponse(res, payload => {
            const raw = payload?.data ?? payload
            return Array.isArray(raw) ? (raw as HandoverRequest[]) : []
          })
        )
    },

    // Get sessions assigned to the current agent (optionally include team sessions)
    getAssignedSessions: (params?: {
      page?: number
      limit?: number
      includeTeam?: boolean
    }): Promise<ApiResponse<AgentAssignedSessionsPage>> => {
      const client = createSessionsClient()
      const query: Record<string, string> = {}

      if (params?.page && Number.isFinite(params.page)) {
        query.page = String(params.page)
      }
      if (params?.limit && Number.isFinite(params.limit)) {
        query.limit = String(params.limit)
      }
      if (params?.includeTeam !== undefined) {
        query.includeTeam = params.includeTeam ? 'true' : 'false'
      }

      return client.assigned
        .$get(Object.keys(query).length ? { query } : undefined)
        .then(res =>
          handleHonoResponse(res, payload => {
            const raw = payload?.data ?? payload ?? {}
            const sessions = Array.isArray(raw.sessions) ? raw.sessions : []
            const rawPage =
              typeof raw.page === 'number'
                ? raw.page
                : Number.parseInt(String(raw.page ?? ''), 10)
            const rawLimit =
              typeof raw.limit === 'number'
                ? raw.limit
                : Number.parseInt(String(raw.limit ?? ''), 10)
            const parsedTotal =
              typeof raw.total === 'number'
                ? raw.total
                : raw.total != null && !Number.isNaN(Number(raw.total))
                  ? Number(raw.total)
                  : undefined

            return {
              sessions,
              page:
                !Number.isNaN(rawPage) && Number.isFinite(rawPage)
                  ? rawPage
                  : (params?.page ?? 1),
              limit:
                !Number.isNaN(rawLimit) && Number.isFinite(rawLimit)
                  ? rawLimit
                  : (params?.limit ?? 30),
              hasMore: Boolean(raw.hasMore),
              total: parsedTotal ?? sessions.length,
            }
          })
        )
    },

    // Get sessions for team sidebar with server-side filtering of empty sessions
    getTeamSessions: (params?: {
      page?: number
      limit?: number
      includeTeam?: boolean
      onlyWithVisibleMessages?: boolean
      channels?: string[]
      agentIds?: number[]
      includeUnassigned?: boolean
      dateFrom?: string
      dateTo?: string
      search?: string
    }): Promise<ApiResponse<AgentAssignedSessionsPage>> => {
      const client = createSessionsClient()
      const query: Record<string, string> = {
        onlyWithVisibleMessages:
          params?.onlyWithVisibleMessages === false ? 'false' : 'true',
      }

      if (params?.page && Number.isFinite(params.page)) {
        query.page = String(params.page)
      }
      if (params?.limit && Number.isFinite(params.limit)) {
        query.limit = String(params.limit)
      }
      if (params?.includeTeam !== undefined) {
        query.includeTeam = params.includeTeam ? 'true' : 'false'
      }
      if (params?.channels && params.channels.length > 0) {
        query.channels = params.channels.join(',')
      }
      if (params?.agentIds && params.agentIds.length > 0) {
        query.agentIds = params.agentIds.join(',')
      }
      if (params?.includeUnassigned === false) {
        query.includeUnassigned = 'false'
      }
      if (params?.dateFrom) {
        query.dateFrom = params.dateFrom
      }
      if (params?.dateTo) {
        query.dateTo = params.dateTo
      }
      if (params?.search && params.search.length > 0) {
        query.search = params.search
      }

      return client['list_by_team'].$get({ query }).then(res =>
        handleHonoResponse(res, payload => {
          const raw = payload?.data ?? payload ?? {}
          const sessions = Array.isArray(raw.sessions) ? raw.sessions : []
          const rawPage =
            typeof raw.page === 'number'
              ? raw.page
              : Number.parseInt(String(raw.page ?? ''), 10)
          const rawLimit =
            typeof raw.limit === 'number'
              ? raw.limit
              : Number.parseInt(String(raw.limit ?? ''), 10)
          const parsedTotal =
            typeof raw.total === 'number'
              ? raw.total
              : raw.total != null && !Number.isNaN(Number(raw.total))
                ? Number(raw.total)
                : undefined

          return {
            sessions,
            page:
              !Number.isNaN(rawPage) && Number.isFinite(rawPage)
                ? rawPage
                : (params?.page ?? 1),
            limit:
              !Number.isNaN(rawLimit) && Number.isFinite(rawLimit)
                ? rawLimit
                : (params?.limit ?? 30),
            hasMore: Boolean(raw.hasMore),
            total: parsedTotal ?? sessions.length,
          }
        })
      )
    },

    // Get all active sessions (with optional team filtering)
    getAllActiveSessions: (
      teamId?: number
    ): Promise<ApiResponse<HandoverRequest[]>> => {
      const client = createSessionsClient()
      return client['all-active']
        .$get(
          teamId
            ? {
                query: {
                  teamId: teamId.toString(),
                },
              }
            : undefined
        )
        .then(res =>
          handleHonoResponse(res, payload => {
            const raw = payload?.data ?? payload
            return Array.isArray(raw) ? (raw as HandoverRequest[]) : []
          })
        )
    },

    // Get full session details for a specific session
    getSession: (sessionId: string): Promise<ApiResponse<ChatSession>> =>
      apiRequest<ChatSession>(`/sessions/${sessionId}`),

    // Complete a session on behalf of the current agent
    completeSession: (sessionId: string): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/sessions/${sessionId}/complete`, {
        method: 'POST',
      }),

    // Get available agents for session assignment
    getAvailableAgents: (
      sessionId: string,
      enforceTeamConstraints: boolean = true
    ): Promise<ApiResponse<AgentListItem[]>> => {
      const client = createSessionsClient()
      return client[':sessionId']['available-agents']
        .$get({
          param: { sessionId },
          query: {
            enforceTeamConstraints: enforceTeamConstraints.toString(),
          },
        })
        .then(res =>
          handleHonoResponse(res, payload => {
            const raw = payload?.data ?? payload
            return Array.isArray(raw) ? (raw as AgentListItem[]) : []
          })
        )
    },

    // Get available agents for handover request
    getAvailableAgentsForHandover: (
      requestId: number,
      enforceTeamConstraints: boolean = true
    ): Promise<ApiResponse<AgentListItem[]>> => {
      const client = createSessionsClient()
      return client.handovers[':requestId']['available-agents']
        .$get({
          param: { requestId },
          query: {
            enforceTeamConstraints: enforceTeamConstraints.toString(),
          },
        })
        .then(res =>
          handleHonoResponse(res, payload => {
            const raw = payload?.data ?? payload
            return Array.isArray(raw) ? (raw as AgentListItem[]) : []
          })
        )
    },

    // Assign handover request to agent
    assignHandover: (requestId: number): Promise<ApiResponse<void>> => {
      const client = createSessionsClient()
      return client.handovers[':requestId'].assign
        .$post({ param: { requestId }, json: {} })
        .then(res => handleHonoResponse(res, () => undefined))
    },

    // Take over a session
    takeoverSession: (
      sessionId: string,
      options?: SessionTakeoverOptions
    ): Promise<ApiResponse<SessionActionResponse>> => {
      const client = createSessionsClient()
      const requestOptions: {
        param: { sessionId: string }
        json?: SessionTakeoverOptions
      } =
        options && Object.keys(options).length > 0
          ? { param: { sessionId }, json: options }
          : { param: { sessionId } }
      return client[':sessionId'].takeover
        .$post(requestOptions)
        .then(res =>
          handleHonoResponse(
            res,
            payload => payload?.data ?? ({} as JsonObject)
          )
        )
    },

    // Transfer session to another agent
    transferSession: (
      sessionId: string,
      targetAgentId: number,
      reason?: string,
      priority?: 'low' | 'normal' | 'high' | 'urgent',
      teamId?: number
    ): Promise<ApiResponse<SessionActionResponse>> => {
      const client = createSessionsClient()
      return client[':sessionId'].transfer
        .$post({
          param: { sessionId },
          json: {
            targetAgentId,
            reason,
            priority: priority ?? 'normal',
            teamIds: teamId != null ? [teamId] : undefined,
          },
        })
        .then(res =>
          handleHonoResponse(
            res,
            payload => payload?.data ?? ({} as JsonObject)
          )
        )
    },

    // Transfer session to a team (any agent can accept)
    transferSessionToTeam: (
      sessionId: string,
      targetTeamId: number,
      reason?: string,
      priority?: 'low' | 'normal' | 'high' | 'urgent'
    ): Promise<ApiResponse<SessionActionResponse>> => {
      return apiRequest<SessionActionResponse>(
        `/sessions/${sessionId}/transfer-to-team`,
        {
          method: 'POST',
          body: JSON.stringify({
            targetTeamIds: targetTeamId, // Send as single number (unified endpoint)
            reason,
            priority: priority ?? 'normal',
          }),
        }
      )
    },

    // Transfer session to multiple teams (any agent from any team can accept)
    transferSessionToMultipleTeams: (
      sessionId: string,
      targetTeamIds: number[],
      reason?: string,
      priority?: 'low' | 'normal' | 'high' | 'urgent'
    ): Promise<ApiResponse<SessionActionResponse>> => {
      return apiRequest<SessionActionResponse>(
        `/sessions/${sessionId}/transfer-to-team`,
        {
          method: 'POST',
          body: JSON.stringify({
            targetTeamIds, // Send as array (unified endpoint)
            reason,
            priority: priority ?? 'normal',
          }),
        }
      )
    },

    // Cancel a pending transfer request
    cancelTransfer: (
      sessionId: string,
      handoverRequestId: number
    ): Promise<ApiResponse<SessionActionResponse>> => {
      const client = createSessionsClient()
      return client[':sessionId'].transfer[':handoverRequestId'].cancel
        .$post({
          param: {
            sessionId,
            handoverRequestId: handoverRequestId.toString(),
          },
        })
        .then(res =>
          handleHonoResponse(
            res,
            payload => payload?.data ?? ({} as JsonObject)
          )
        )
    },

    // Cancel a pending handover request
    cancelHandover: (
      requestId: number
    ): Promise<ApiResponse<SessionActionResponse>> => {
      const client = createSessionsClient()
      return client.handovers[':requestId'].cancel
        .$post({
          param: {
            requestId: requestId,
          },
        })
        .then(res =>
          handleHonoResponse(
            res,
            payload => payload?.data ?? ({} as JsonObject)
          )
        )
    },

    // Cancel all pending handover requests for the current agent
    cancelPendingHandovers: (): Promise<ApiResponse<SessionActionResponse>> => {
      const client = createSessionsClient()
      return client['cancel-pending-handovers']
        .$post()
        .then(res =>
          handleHonoResponse(
            res,
            payload => payload?.data ?? ({} as JsonObject)
          )
        )
    },

    // Get transfer status for a session
    getTransferStatus: (
      sessionId: string
    ): Promise<ApiResponse<SessionActionResponse>> => {
      const client = createSessionsClient()
      return client[':sessionId']['transfer-status']
        .$get({ param: { sessionId } })
        .then(res =>
          handleHonoResponse(res, payload => payload?.data ?? payload ?? {})
        )
    },

    // Get available agents for transfer
    getAvailableAgentsForTransfer: (
      sessionId: string
    ): Promise<ApiResponse<AvailableAgentsResponse>> => {
      const client = createSessionsClient()
      return client[':sessionId']['available-agents-for-transfer']
        .$get({ param: { sessionId } })
        .then(res =>
          handleHonoResponse(res, payload => {
            const raw = payload?.data ?? payload
            if (
              raw &&
              typeof raw === 'object' &&
              'agents' in raw &&
              Array.isArray((raw as Record<string, unknown>).agents)
            ) {
              return {
                agents: (raw as { agents: AgentListItem[] }).agents,
              }
            }
            return { agents: [] }
          })
        )
    },

    // Accept a pending transfer request
    acceptTransfer: (
      sessionId: string
    ): Promise<
      ApiResponse<{
        assignmentId: number
        sessionId: string
        agentId: number
      }>
    > =>
      apiRequest<{
        assignmentId: number
        sessionId: string
        agentId: number
      }>(`/agent/notifications/accept-transfer/${sessionId}`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),

    // Get visitors (with optional team filtering)
    getVisitors: (teamId?: number): Promise<ApiResponse<SessionVisitor[]>> => {
      const client = createSessionsClient()
      return client.visitors
        .$get(
          teamId
            ? {
                query: {
                  teamId: teamId.toString(),
                },
              }
            : undefined
        )
        .then(res => handleHonoResponse(res, payload => payload?.data ?? []))
    },

    // Initiate chat with visitor
    initiateVisitorChat: (
      visitorId: string
    ): Promise<ApiResponse<{ sessionId: string; message?: string }>> => {
      const client = createSessionsClient()
      return client['initiate-visitor-chat']
        .$post({
          json: { visitorId },
        })
        .then(res => handleHonoResponse(res, payload => payload?.data ?? {}))
    },

    getConversationsCountReport: (filters?: {
      dateRange?: string
      agent?: string
      userType?: string
    }): Promise<ApiResponse<ConversationCountReport>> => {
      const client = createSessionsClient()
      const query: Record<string, string> = {}

      if (filters?.dateRange) {
        query.dateRange = filters.dateRange
      }
      if (filters?.agent) {
        query.agent = filters.agent
      }
      if (filters?.userType) {
        query.userType = filters.userType
      }

      const options =
        Object.keys(query).length > 0
          ? {
              query,
            }
          : undefined

      return client.reports.conversations.count
        .$get(options)
        .then(res =>
          handleHonoResponse(res, payload => payload?.data ?? payload)
        )
    },

    getConversationsPerformanceReport: (filters?: {
      dateRange?: string
      agent?: string
      userType?: string
      country?: string
      language?: string
      browser?: string
      os?: string
    }): Promise<ApiResponse<ConversationPerformanceReport>> => {
      const client = createSessionsClient()
      const query: Record<string, string> = {}

      if (filters?.dateRange) {
        query.dateRange = filters.dateRange
      }
      if (filters?.agent) {
        query.agent = filters.agent
      }
      if (filters?.userType) {
        query.userType = filters.userType
      }
      if (filters?.country) {
        query.country = filters.country
      }
      if (filters?.language) {
        query.language = filters.language
      }
      if (filters?.browser) {
        query.browser = filters.browser
      }
      if (filters?.os) {
        query.os = filters.os
      }

      const options =
        Object.keys(query).length > 0
          ? {
              query,
            }
          : undefined

      return client.reports.conversations.performance
        .$get(options)
        .then(res =>
          handleHonoResponse(res, payload => payload?.data ?? payload)
        )
    },

    // Message template management
    getMessageTemplates: (
      channel?: MessagingChannel
    ): Promise<ApiResponse<MessageTemplate[]>> => {
      const client = createSessionsClient()
      return client.templates
        .$get(
          channel
            ? {
                query: {
                  channel,
                },
              }
            : undefined
        )
        .then(res => handleHonoResponse(res, payload => payload?.data ?? []))
    },

    createMessageTemplate: (
      payload: MessageTemplatePayload
    ): Promise<ApiResponse<MessageTemplate>> => {
      const client = createSessionsClient()
      return client.templates
        .$post({ json: payload })
        .then(res => handleHonoResponse(res, data => data?.data ?? data))
    },

    updateMessageTemplate: (
      templateId: number,
      payload: MessageTemplateUpdatePayload
    ): Promise<ApiResponse<MessageTemplate>> => {
      const client = createSessionsClient()
      return client.templates[':templateId']
        .$put({
          param: {
            templateId: templateId.toString(),
          },
          json: payload,
        })
        .then(res => handleHonoResponse(res, data => data?.data ?? data))
    },

    deleteMessageTemplate: (templateId: number): Promise<ApiResponse<void>> => {
      const client = createSessionsClient()
      return client.templates[':templateId']
        .$delete({
          param: {
            templateId: templateId.toString(),
          },
        })
        .then(res => handleHonoResponse(res, payload => payload?.data))
    },

    // Contacts search (agent sessions scope)
    searchContacts: (opts?: {
      search?: string
      page?: number
      limit?: number
      withPhoneOnly?: boolean
    }): Promise<
      ApiResponse<{
        contacts: ContactRecord[]
        pagination: {
          page: number
          limit: number
          total: number
          pages: number
          hasMore: boolean
        }
      }>
    > => {
      const client = createSessionsClient()
      const query: Record<string, string> = {}
      if (opts?.search) query.search = opts.search
      if (opts?.page) query.page = String(opts.page)
      if (opts?.limit) query.limit = String(opts.limit)
      if (opts?.withPhoneOnly !== undefined)
        query.withPhoneOnly = String(opts.withPhoneOnly)
      return client.contacts
        .$get({ query })
        .then(res => handleHonoResponse(res, payload => payload?.data))
    },

    createContact: (payload: {
      firstName: string
      lastName?: string
      title?: string
      email?: string
      phoneNumber?: string
      fbMessengerId?: string
      telegramId?: string
      instagramId?: string
      weChatId?: string
      lineId?: string
    }): Promise<ApiResponse<ContactRecord>> => {
      const client = createSessionsClient()
      return client.contacts
        .$post({ json: payload })
        .then(res => handleHonoResponse(res, data => data?.data ?? data))
    },

    updateContact: (
      contactId: number,
      payload: {
        firstName?: string
        lastName?: string
        title?: string
        email?: string
        phoneNumber?: string
        fbMessengerId?: string
        telegramId?: string
        instagramId?: string
        weChatId?: string
        lineId?: string
      }
    ): Promise<ApiResponse<ContactRecord>> => {
      const client = createSessionsClient()
      return client.contacts[':contactId']
        .$put({
          param: { contactId: contactId.toString() },
          json: payload,
        })
        .then(res => handleHonoResponse(res, data => data?.data ?? data))
    },

    deleteContact: (contactId: number): Promise<ApiResponse<void>> => {
      const client = createSessionsClient()
      return client.contacts[':contactId']
        .$delete({ param: { contactId: contactId.toString() } })
        .then(res => handleHonoResponse(res, payload => payload?.data))
    },

    // Channel messaging helpers
    getFacebookContacts: (): Promise<
      ApiResponse<FacebookConversationContact[]>
    > => {
      const client = createSessionsClient()
      return client.channels.facebook.contacts
        .$get()
        .then(res => handleHonoResponse(res, payload => payload?.data ?? []))
    },

    fetchWhatsAppTemplates: (): Promise<
      ApiResponse<WhatsAppTemplateRecord[]>
    > => {
      const client = createSessionsClient()
      return client.channels.whatsapp.templates.fetch.$get().then(res =>
        handleHonoResponse(res, payload => {
          const raw = payload?.data ?? payload
          return Array.isArray(raw) ? (raw as WhatsAppTemplateRecord[]) : []
        })
      )
    },

    initiateChannelMessage: (payload: {
      channel: MessagingChannel
      recipient: string
      templateId?: number
      message?: string
      components?: WhatsAppTemplateComponent[]
      parameters?: string[]
      mediaUrl?: string
      mediaType?: 'photo' | 'image' | 'video' | 'audio' | 'document'
    }): Promise<
      ApiResponse<{ sessionId: string; expiresAt: string | null }>
    > => {
      const client = createSessionsClient()
      return client.channels.initiate
        .$post({ json: payload })
        .then(res => handleHonoResponse(res, data => data?.data ?? data))
    },

    // Message translations
    getMessageTranslation: (
      sessionId: string,
      messageId: string,
      locale = 'en'
    ): Promise<
      ApiResponse<{ messageId: string; locale: string; content: string } | null>
    > => {
      const client = createSessionsClient()
      return client[':sessionId'].messages[':messageId'].translation
        .$get({
          param: { sessionId, messageId },
          query: { locale },
        })
        .then(res => handleHonoResponse(res, data => data?.data))
    },

    getSessionTranslations: (
      sessionId: string,
      locale = 'en'
    ): Promise<ApiResponse<Record<string, string>>> => {
      const client = createSessionsClient()
      return client[':sessionId'].translations
        .$get({
          param: { sessionId },
          query: { locale },
        })
        .then(res => handleHonoResponse(res, data => data?.data ?? {}))
    },

    // Supervisor handover management
    getSupervisorHandoverRequests: (
      status?: string
    ): Promise<ApiResponse<HandoverRequest[]>> => {
      const params = status ? `?status=${status}` : ''
      return apiRequest<HandoverRequest[]>(
        `/admin/supervisor/handovers${params}`
      )
    },

    getAvailableAgentsForAssignment: (): Promise<
      ApiResponse<AgentListItem[]>
    > =>
      apiRequest<AgentListItem[]>('/admin/supervisor/available-agents').then(
        r => r
      ),

    assignHandoverToAgent: (
      requestId: number,
      agentId: number,
      options?: { teamId?: number }
    ): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/supervisor/handovers/${requestId}/assign`, {
        method: 'POST',
        body: JSON.stringify({
          agentId,
          teamId:
            typeof options?.teamId === 'number' &&
            Number.isFinite(options.teamId)
              ? options.teamId
              : undefined,
        }),
      }),

    reassignHandover: (
      requestId: number,
      newAgentId: number,
      options?: { newTeamId?: number; reason?: string }
    ): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/admin/supervisor/handovers/${requestId}/reassign`, {
        method: 'PUT',
        body: JSON.stringify({
          newAgentId,
          newTeamId:
            typeof options?.newTeamId === 'number' &&
            Number.isFinite(options.newTeamId)
              ? options.newTeamId
              : undefined,
          reason: options?.reason,
        }),
      }),
  },

  // Agent-specific APIs
  agent: {
    // Get pending handover assignments for the logged-in agent
    getPendingHandoverAssignments: (): Promise<
      ApiResponse<HandoverRequest[]>
    > =>
      apiRequest<HandoverRequest[]>('/agent/handover/assignments/pending').then(
        r => r
      ),
    // Update agent online status
    updateStatus: (isOnline: boolean): Promise<ApiResponse<void>> =>
      apiRequest<void>('/agent/status', {
        method: 'POST',
        body: JSON.stringify({ isOnline }),
      }),

    // Agent responds to handover assignment (accept/reject)
    respondToHandoverAssignment: (
      requestId: number,
      data: {
        response: 'accepted' | 'rejected'
        reason?: string
        teamId?: number
      }
    ): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/agent/handover/assignments/${requestId}/respond`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listAgents: (): Promise<ApiResponse<AgentListItem[]>> =>
      apiRequest<AgentListItem[]>('/agent/list'),

    // Get handover assignment history for the agent
    getHandoverHistory: (options?: {
      scope?: 'me' | 'teams' | 'team'
      teamId?: number
    }): Promise<ApiResponse<HandoverRequest[]>> => {
      const params = new URLSearchParams()

      if (options?.scope) {
        params.set('scope', options.scope)
      }

      if (
        options?.teamId !== undefined &&
        options.teamId !== null &&
        !Number.isNaN(options.teamId)
      ) {
        params.set('teamId', String(options.teamId))
      }

      const queryString = params.toString()
      const url = `/agent/handover/assignments/history${
        queryString.length > 0 ? `?${queryString}` : ''
      }`

      return apiRequest<HandoverRequest[]>(url).then(r => r)
    },

    // Get teams for agent
    getTeams: (): Promise<ApiResponse<Team[]>> =>
      apiRequest<Team[]>('/agent/teams'),
    // Get all teams (for assignment etc)
    getAllTeams: (): Promise<ApiResponse<Team[]>> =>
      apiRequest<Team[]>('/agent/all-teams'),
    // Send a message to a session (supports both agent and system messages)
    sendSessionMessage: (
      sessionId: string,
      data: {
        content: string
        type: string
        fileUrl?: string
        fileName?: string
        audioUrl?: string
        imageUrl?: string
        role?: string // Optional: 'system' for system messages
      }
    ): Promise<ApiResponse<{ messageId: string }>> =>
      apiRequest<{ messageId: string }>(`/sessions/${sessionId}/message`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listArticles: (params?: {
      search?: string
      page?: number
      pageSize?: number
    }): Promise<ApiResponse<Article[]>> => {
      const searchParams = new URLSearchParams()
      if (params?.search) {
        searchParams.set('search', params.search)
      }
      if (params?.page) {
        searchParams.set('page', String(params.page))
      }
      if (params?.pageSize) {
        searchParams.set('pageSize', String(params.pageSize))
      }
      const queryString = searchParams.toString()

      return apiRequest<Article[]>(
        `/agent/articles${queryString ? `?${queryString}` : ''}`
      )
    },

    getArticle: (articleId: number): Promise<ApiResponse<Article>> =>
      apiRequest<Article>(`/agent/articles/${articleId}`),

    createArticle: (payload: ArticlePayload): Promise<ApiResponse<Article>> =>
      apiRequest<Article>('/agent/articles', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateArticle: (
      articleId: number,
      payload: ArticleUpdatePayload
    ): Promise<ApiResponse<Article>> =>
      apiRequest<Article>(`/agent/articles/${articleId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),

    deleteArticle: (articleId: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/agent/articles/${articleId}`, {
        method: 'DELETE',
      }),

    searchArticles: (
      query: string,
      options?: { limit?: number }
    ): Promise<ApiResponse<Article[]>> => {
      const searchParams = new URLSearchParams({ query })
      if (options?.limit) {
        searchParams.set('limit', String(options.limit))
      }

      return apiRequest<Article[]>(
        `/agent/articles/suggestions?${searchParams.toString()}`
      )
    },

    askKnowledge: (payload: {
      query: string
      sessionId?: string
      maxResults?: number
      minScore?: number
      includeContext?: boolean
    }): Promise<ApiResponse<KnowledgeBaseAnswer>> =>
      apiRequest<KnowledgeBaseAnswer>('/knowledge-base/ask', {
        method: 'POST',
        body: JSON.stringify({
          query: payload.query,
          sessionId: payload.sessionId,
          maxResults: payload.maxResults ?? 5,
          minScore: payload.minScore ?? 0.3,
          includeContext: payload.includeContext ?? true,
        }),
      }),
    // Notification APIs
    getNotifications: (options?: {
      includeRead?: boolean
      includeDismissed?: boolean
      limit?: number
      offset?: number
    }): Promise<ApiResponse<NotificationRecord[]>> => {
      const params = new URLSearchParams()
      if (options?.includeRead !== undefined)
        params.set('includeRead', String(options.includeRead))
      if (options?.includeDismissed !== undefined)
        params.set('includeDismissed', String(options.includeDismissed))
      if (options?.limit !== undefined)
        params.set('limit', String(options.limit))
      if (options?.offset !== undefined)
        params.set('offset', String(options.offset))

      const query = params.toString()
      return apiRequest<NotificationRecord[]>(
        `/agent/notifications${query ? `?${query}` : ''}`
      )
    },

    getUnreadNotificationCount: (): Promise<ApiResponse<{ count: number }>> =>
      apiRequest<{ count: number }>('/agent/notifications/unread-count'),

    markNotificationAsRead: (
      notificationId: number
    ): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/agent/notifications/${notificationId}/read`, {
        method: 'PUT',
      }),

    markAllNotificationsAsRead: (): Promise<ApiResponse<void>> =>
      apiRequest<void>('/agent/notifications/mark-all-read', {
        method: 'POST',
      }),

    dismissNotification: (notificationId: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/agent/notifications/${notificationId}/dismiss`, {
        method: 'DELETE',
      }),
    deleteNotification: (notificationId: number): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/agent/notifications/${notificationId}`, {
        method: 'DELETE',
      }),

    acceptSessionTransfer: (
      sessionId: string
    ): Promise<
      ApiResponse<{
        assignmentId: number
        sessionId: string
        agentId: number
      }>
    > =>
      apiRequest<{
        assignmentId: number
        sessionId: string
        agentId: number
      }>(`/agent/notifications/accept-transfer/${sessionId}`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
  },

  // Chat related helpers (keeps compatibility with older call sites)
  chat: {
    // Submit a form payload from chat UI
    submitForm: (
      data: JsonObject
    ): Promise<ApiResponse<SessionActionResponse>> =>
      apiRequest<SessionActionResponse>('/chat/form-submit', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    requestHandover: (data: {
      sessionId: string
      reason?: string
      priority?: 'low' | 'normal' | 'high' | 'urgent'
      teamId?: number | null
      teamName?: string | null
      specificAgentId?: number
      sendToAllAgents?: boolean
      autoAssign?: boolean
    }): Promise<ApiResponse<SessionActionResponse>> =>
      apiRequest<SessionActionResponse>(
        `/sessions/${data.sessionId}/handover`,
        {
          method: 'PUT',
          body: JSON.stringify(
            (() => {
              const payload: Record<string, JsonValue> = {
                requestedBy: 'bot',
                reason: data.reason || 'Customer needs human assistance',
                priority: data.priority || 'normal',
                teamId:
                  typeof data.teamId === 'number' &&
                  Number.isFinite(data.teamId)
                    ? data.teamId
                    : null,
                teamName: data.teamName ?? null,
                assignedTo: data.specificAgentId || null,
                notes: `Handover requested from frontend`,
                sendToAllAgents: data.sendToAllAgents || false,
                autoAssign: data.autoAssign || false,
              }
              return payload
            })()
          ),
        }
      ),

    getSessionStatus: (
      sessionId: string
    ): Promise<
      ApiResponse<{
        sessionId: string
        isHandedOver: boolean
        agentName: string | null
        status?: string
        platform?: string
      }>
    > => apiRequest(`/sessions/${sessionId}/status`),

    getTransferStatus: (
      sessionId: string
    ): Promise<
      ApiResponse<
        | (HandoverRequest & {
            fromAgentName?: string
            toAgentName?: string
          })
        | null
      >
    > =>
      apiRequest<{
        transferRequest:
          | (HandoverRequest & {
              fromAgentName?: string
              toAgentName?: string
            })
          | null
      }>(`/sessions/${sessionId}/transfer-status`).then(response => ({
        ...response,
        data: response.data?.transferRequest ?? null,
      })),

    getSessionMessages: (
      sessionId: string
    ): Promise<ApiResponse<ChatMessage[]>> =>
      apiRequest<{ messages: ChatMessage[] }>(
        `/sessions/${sessionId}/messages`
      ).then(response => ({
        ...response,
        data: Array.isArray(response.data?.messages)
          ? response.data.messages
          : [],
      })),
  },

  publicBots: {
    // Retrieve the default bot for the current site (public endpoint)
    getDefault: (): Promise<ApiResponse<Bot>> =>
      apiRequest<Bot>('/bots/default'),
  },

  voice: {
    realtime: {
      listConfigs: (): Promise<ApiResponse<VoiceRealtimeConfigSummary[]>> =>
        apiRequest<VoiceRealtimeConfigSummary[]>(`/chat/voice/configs`),
      createSession: (
        data: VoiceRealtimeSessionRequest
      ): Promise<ApiResponse<VoiceRealtimeSessionResponseData>> =>
        apiRequest<VoiceRealtimeSessionResponseData>(`/chat/voice/sessions`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      executeTool: (data: {
        sessionId: string
        toolName: string
        toolArgs: Record<string, JsonValue>
        botId?: number
        languageCode?: string
      }): Promise<ApiResponse<FlowExecutionResult>> =>
        apiRequest<FlowExecutionResult>(`/chat/voice/tools/execute`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
    },
  },

  // Flow Execution APIs
  flows: {
    // Get active flow for default site
    getActiveFlow: (): Promise<ApiResponse<Flow>> => {
      return apiRequest<Flow>('/chat/flows/active')
    },

    // Initialize flow session
    initialize: (data: {
      sessionId: string
      triggerContext: string
      userMessage?: string
    }): Promise<ApiResponse<FlowExecutionResult>> =>
      apiRequest<FlowExecutionResult>('/chat/flows/initialize', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Trigger flow entry
    trigger: (data: {
      sessionId: string
      triggerContext: string
    }): Promise<ApiResponse<FlowExecutionResult>> =>
      apiRequest<FlowExecutionResult>('/chat/flows/trigger', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Execute flow step
    execute: (data: {
      sessionId: string
      userInput?: string
      displayText?: string
      nodeId: string
      platform?: string
    }): Promise<ApiResponse<FlowExecutionResult>> =>
      apiRequest<FlowExecutionResult>('/chat/flows/execute', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Reset flow session
    reset: (data: {
      sessionId: string
      flowId?: number
      flowSlug?: string
    }): Promise<ApiResponse<void>> => {
      const queryParams = new URLSearchParams()
      if (data.flowId) queryParams.set('flowId', String(data.flowId))
      if (data.flowSlug) queryParams.set('flowSlug', data.flowSlug)
      const queryString = queryParams.toString()
      const url = queryString
        ? `/chat/flows/reset?${queryString}`
        : '/chat/flows/reset'

      return apiRequest<void>(url, {
        method: 'POST',
        body: JSON.stringify({ sessionId: data.sessionId }),
      })
    },

    // Route flow with LLM
    routeWithLLM: (data: {
      sessionId: string
      systemMessage: string
      userMessage?: string
      currentFlowName?: string
    }): Promise<ApiResponse<FlowExecutionResult>> =>
      apiRequest<FlowExecutionResult>('/chat/flows/route-llm', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Get flow sessions
    getSessions: (
      sessionId: string
    ): Promise<ApiResponse<FlowSessionRecord[]>> =>
      apiRequest<FlowSessionRecord[]>(`/chat/flows/sessions/${sessionId}`),

    // Delete flow session
    deleteSession: (sessionId: string): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/chat/flows/sessions/${sessionId}`, {
        method: 'DELETE',
      }),

    // Debug session
    debugSession: (
      sessionId: string
    ): Promise<ApiResponse<FlowExecutionResult>> =>
      apiRequest<FlowExecutionResult>(
        `/chat/flows/debug-session/${sessionId}`
      ).then(r => r),
  },

  // Public Booking API - ERPNext Custom APIs
  publicBooking: {
    // Base URL uses Next.js API proxy
    _baseUrl: (): string => {
      return '/api/public-booking'
    },

    // Ping endpoint
    ping: async (): Promise<
      ApiResponse<{ status: string; message: string; timestamp: string }>
    > => {
      const response = await fetch(`${api.publicBooking._baseUrl()}.ping`)
      const data = await response.json()
      if (!response.ok) {
        return {
          success: false,
          error: data.message || 'API Error',
          status: response.status,
        }
      }
      return { success: true, data: data.message, status: response.status }
    },

    // Get departments
    departments: {
      getAll: async (): Promise<
        ApiResponse<Array<{ name: string; department: string }>>
      > => {
        const response = await fetch(
          `${api.publicBooking._baseUrl()}/get_departments`
        )
        const data = await response.json()
        if (!response.ok || !data.message?.success) {
          return {
            success: false,
            error: data.message?.message || 'Failed to fetch departments',
            status: response.status,
          }
        }
        return {
          success: true,
          data: data.message.data,
          status: response.status,
        }
      },
    },

    // Get practitioners
    practitioners: {
      getAll: async (department?: string): Promise<ApiResponse<any[]>> => {
        const url = new URL(
          `${api.publicBooking._baseUrl()}/get_practitioners`,
          window.location.origin
        )
        if (department) {
          url.searchParams.append('department', department)
        }
        const response = await fetch(url.toString())
        const data = await response.json()
        if (!response.ok || !data.message?.success) {
          return {
            success: false,
            error: data.message?.message || 'Failed to fetch practitioners',
            status: response.status,
          }
        }
        return {
          success: true,
          data: data.message.data,
          status: response.status,
        }
      },
    },

    // Get services
    services: {
      getAll: async (): Promise<
        ApiResponse<
          Array<{
            name: string
            appointment_type: string
            default_duration: number
          }>
        >
      > => {
        const response = await fetch(
          `${api.publicBooking._baseUrl()}/get_services`
        )
        const data = await response.json()
        if (!response.ok || !data.message?.success) {
          return {
            success: false,
            error: data.message?.message || 'Failed to fetch services',
            status: response.status,
          }
        }
        return {
          success: true,
          data: data.message.data,
          status: response.status,
        }
      },
    },

    // Get service units
    practitionerSchedules: {
      getAll: async (
        practitioner: string
      ): Promise<
        ApiResponse<
          Array<{
            schedule_mapping_id: string
            schedule: string
            service_unit: string
            service_unit_name: string
            service_unit_type?: string
          }>
        >
      > => {
        const url = new URL(
          `${api.publicBooking._baseUrl()}/get_practitioner_schedules`,
          window.location.origin
        )
        url.searchParams.append('practitioner', practitioner)
        const response = await fetch(url.toString())
        return response.json()
      },
    },
    serviceUnits: {
      getAll: async (
        practitioner?: string
      ): Promise<
        ApiResponse<
          Array<{
            name: string
            healthcare_service_unit_name: string
            service_unit_type?: string
          }>
        >
      > => {
        const url = new URL(
          `${api.publicBooking._baseUrl()}/get_service_units`,
          window.location.origin
        )
        if (practitioner) {
          url.searchParams.append('practitioner', practitioner)
        }
        const response = await fetch(url.toString())
        const data = await response.json()
        if (!response.ok || !data.message?.success) {
          return {
            success: false,
            error: data.message?.message || 'Failed to fetch service units',
            status: response.status,
          }
        }
        return {
          success: true,
          data: data.message.data,
          status: response.status,
        }
      },
    },

    // Get available slots
    appointments: {
      getAvailableSlots: async (params: {
        practitioner: string
        date?: string
        date_from?: string
        date_to?: string
        schedule?: string
      }): Promise<ApiResponse<any[]>> => {
        const url = new URL(
          `${api.publicBooking._baseUrl()}/get_available_slots`,
          window.location.origin
        )
        url.searchParams.append('practitioner', params.practitioner)
        if (params.date) {
          url.searchParams.append('date', params.date)
        }
        if (params.date_from) {
          url.searchParams.append('date_from', params.date_from)
        }
        if (params.date_to) {
          url.searchParams.append('date_to', params.date_to)
        }
        if (params.schedule) {
          url.searchParams.append('schedule', params.schedule)
        }
        const response = await fetch(url.toString())
        const data = await response.json()
        if (!response.ok || !data.message?.success) {
          return {
            success: false,
            error: data.message?.message || 'Failed to fetch available slots',
            status: response.status,
          }
        }
        return {
          success: true,
          data: data.message.data,
          status: response.status,
        }
      },

      // Create booking
      create: async (bookingData: {
        patient_name: string
        mobile: string
        email?: string
        practitioner: string
        appointment_date: string
        appointment_time: string
        department?: string
        service?: string
        notes?: string
      }): Promise<ApiResponse<any>> => {
        const response = await fetch(
          `${api.publicBooking._baseUrl()}/create_booking`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bookingData),
          }
        )
        const data = await response.json()
        if (!response.ok || !data.message?.success) {
          return {
            success: false,
            error: data.message?.message || 'Failed to create booking',
            status: response.status,
          }
        }
        return {
          success: true,
          data: data.message.data,
          status: response.status,
        }
      },

      // Get appointment details
      getById: async (appointmentId: string): Promise<ApiResponse<any>> => {
        const url = new URL(
          `${api.publicBooking._baseUrl()}/get_appointment_details`,
          window.location.origin
        )
        url.searchParams.append('appointment_id', appointmentId)
        const response = await fetch(url.toString())
        const data = await response.json()
        if (!response.ok || !data.message?.success) {
          return {
            success: false,
            error:
              data.message?.message || 'Failed to fetch appointment details',
            status: response.status,
          }
        }
        return {
          success: true,
          data: data.message.data,
          status: response.status,
        }
      },
    },
  },

  // Healthcare APIs
  healthcare: {
    // Internal helper to decide prefix (public embed vs admin dashboard)
    // When embedding the public appointment page, we want to hit '/healthcare' not '/admin/healthcare'.
    _prefix(): string {
      if (
        typeof window !== 'undefined' &&
        window.location.pathname.includes('/public/appointment')
      ) {
        return '/healthcare'
      }
      return '/admin/healthcare'
    },
    // Dashboard statistics
    stats: (): Promise<
      ApiResponse<{
        totalPatients: number
        todaysAppointments: number
        pendingAppointments: number
        completedToday: number
      }>
    > => apiRequest(`${api.healthcare._prefix()}/stats`),

    // Appointment APIs
    appointments: {
      // Get recent appointments
      getRecent: (limit?: number): Promise<ApiResponse<any[]>> =>
        apiRequest(
          `${api.healthcare._prefix()}/appointments/recent${limit ? `?limit=${limit}` : ''}`
        ),

      // Get all appointments with filters
      getAll: (filters?: {
        status?: string
        practitioner?: string
        patient?: string
        dateFrom?: string
        dateTo?: string
        limit?: number
        offset?: number
      }): Promise<ApiResponse<any[]>> => {
        const params = new URLSearchParams()
        if (filters?.status) params.append('status', filters.status)
        if (filters?.practitioner)
          params.append('practitioner', filters.practitioner)
        if (filters?.patient) params.append('patient', filters.patient)
        if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
        if (filters?.dateTo) params.append('dateTo', filters.dateTo)
        if (filters?.limit) params.append('limit', filters.limit.toString())
        if (filters?.offset) params.append('offset', filters.offset.toString())
        const query = params.toString()
        return apiRequest(
          `${api.healthcare._prefix()}/appointments${query ? `?${query}` : ''}`
        )
      },

      // Get appointment by ID
      getById: (id: string): Promise<ApiResponse<any>> =>
        apiRequest(`${api.healthcare._prefix()}/appointments/${id}`),

      // Create new appointment
      create: (data: {
        patient: string
        practitioner: string
        appointment_type?: string
        appointment_date: string
        appointment_time: string
        duration?: number
        notes?: string
        status?: string
      }): Promise<ApiResponse<any>> =>
        apiRequest(`${api.healthcare._prefix()}/appointments`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      // Update appointment
      update: (
        id: string,
        updates: Record<string, any>
      ): Promise<ApiResponse<any>> =>
        apiRequest(`${api.healthcare._prefix()}/appointments/${id}`, {
          method: 'PUT',
          body: JSON.stringify(updates),
        }),

      // Delete/cancel appointment
      delete: (id: string): Promise<ApiResponse<void>> =>
        apiRequest(`${api.healthcare._prefix()}/appointments/${id}`, {
          method: 'DELETE',
        }),

      // Get available time slots for a practitioner
      getAvailableSlots: (params: {
        practitioner: string
        date: string
        slotDuration?: number
      }): Promise<ApiResponse<any[]>> => {
        const query = new URLSearchParams({
          practitioner: params.practitioner,
          date: params.date,
          slot_duration: (params.slotDuration || 30).toString(),
        }).toString()
        return apiRequest(
          `${api.healthcare._prefix()}/available-slots?${query}`
        )
      },
    },

    // Practitioners APIs
    practitioners: {
      getAll: (): Promise<ApiResponse<any[]>> =>
        apiRequest(`${api.healthcare._prefix()}/practitioners`),
    },

    // Services APIs
    services: {
      getAll: (): Promise<ApiResponse<any[]>> =>
        apiRequest(`${api.healthcare._prefix()}/services`),
    },

    // Patients APIs
    patients: {
      getAll: (filters?: {
        email?: string
        name?: string
        limit?: number
      }): Promise<ApiResponse<any[]>> => {
        const params = new URLSearchParams()
        if (filters?.email) params.append('email', filters.email)
        if (filters?.name) params.append('name', filters.name)
        if (filters?.limit) params.append('limit', filters.limit.toString())
        const query = params.toString()
        return apiRequest(
          `${api.healthcare._prefix()}/patients${query ? `?${query}` : ''}`
        )
      },

      searchByEmail: (email: string): Promise<ApiResponse<any[]>> =>
        apiRequest(
          `${api.healthcare._prefix()}/patients?email=${encodeURIComponent(email)}`
        ),

      create: (data: {
        first_name: string
        last_name?: string
        sex?: string
        dob?: string
        email?: string
        mobile?: string
      }): Promise<ApiResponse<any>> =>
        apiRequest(`${api.healthcare._prefix()}/patients`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
    },

    // Departments APIs
    departments: {
      getAll: (): Promise<ApiResponse<any[]>> =>
        apiRequest(`${api.healthcare._prefix()}/departments`),
    },

    // Blood Groups APIs
    bloodGroups: {
      getAll: (): Promise<ApiResponse<string[]>> =>
        apiRequest(`${api.healthcare._prefix()}/blood-groups`),
    },

    // Countries APIs
    countries: {
      getAll: (): Promise<ApiResponse<string[]>> =>
        apiRequest(`${api.healthcare._prefix()}/countries`),
    },
  },

  // Call Campaign APIs (Agent)
  callCampaigns: {
    list: (params?: {
      status?: string
      limit?: number
      offset?: number
    }): Promise<ApiResponse<any[]>> => {
      const searchParams = new URLSearchParams()
      if (params?.status) searchParams.set('status', params.status)
      if (params?.limit) searchParams.set('limit', String(params.limit))
      if (params?.offset) searchParams.set('offset', String(params.offset))
      const qs = searchParams.toString()
      return apiRequest(`/agent/call-campaigns${qs ? `?${qs}` : ''}`)
    },
    get: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${id}`),
    create: (data: {
      name: string
      description?: string
      botId: number
      elevenLabsConfigId: number
      twilioConfigId: number
      scheduledAt?: string | null
      maxConcurrentCalls?: number
      retryFailedCalls?: boolean
      maxRetries?: number
      metadata?: Record<string, unknown>
    }): Promise<ApiResponse<any>> =>
      apiRequest('/agent/call-campaigns', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (
      id: number,
      data: Record<string, unknown>
    ): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${id}`, { method: 'DELETE' }),
    updateStatus: (id: number, status: string): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }),
    trigger: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${id}/trigger`, { method: 'POST' }),
    stats: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${id}/stats`),
    importContacts: (
      id: number,
      contacts: Array<{ phone: string; name: string }>
    ): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${id}/import-contacts`, {
        method: 'POST',
        body: JSON.stringify({ contacts }),
      }),
    listCalls: (
      campaignId: number,
      params?: { status?: string }
    ): Promise<ApiResponse<any[]>> => {
      const qs = params?.status ? `?status=${params.status}` : ''
      return apiRequest(`/agent/call-campaigns/${campaignId}/calls${qs}`)
    },
    getCall: (campaignId: number, callId: number): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${campaignId}/calls/${callId}`),
    removeCall: (
      campaignId: number,
      callId: number
    ): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${campaignId}/calls/${callId}`, {
        method: 'DELETE',
      }),
    triggerCall: (
      campaignId: number,
      callId: number
    ): Promise<ApiResponse<any>> =>
      apiRequest(
        `/agent/call-campaigns/${campaignId}/calls/${callId}/trigger`,
        { method: 'POST' }
      ),
    pollCall: (campaignId: number, callId: number): Promise<ApiResponse<any>> =>
      apiRequest(`/agent/call-campaigns/${campaignId}/calls/${callId}/poll`),
    followUps: (): Promise<ApiResponse<any[]>> =>
      apiRequest('/agent/call-campaigns/follow-ups'),
    // Config lookups
    config: {
      elevenLabs: (): Promise<ApiResponse<any[]>> =>
        apiRequest('/agent/call-campaigns/config/elevenlabs'),
      twilio: (): Promise<ApiResponse<any[]>> =>
        apiRequest('/agent/call-campaigns/config/twilio'),
      bots: (): Promise<ApiResponse<any[]>> =>
        apiRequest('/agent/call-campaigns/config/bots'),
      phoneNumbers: (): Promise<ApiResponse<any[]>> =>
        apiRequest('/agent/call-campaigns/config/phone-numbers'),
    },
  },

  // LLM Playground
  playground: {
    // Sessions
    listSessions: (opts?: {
      limit?: number
      offset?: number
    }): Promise<ApiResponse<any[]>> =>
      apiRequest(
        `/admin/playground/sessions?limit=${opts?.limit ?? 50}&offset=${opts?.offset ?? 0}`
      ),
    getSession: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/sessions/${id}`),
    createSession: (data: any): Promise<ApiResponse<any>> =>
      apiRequest('/admin/playground/sessions', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateSession: (id: number, data: any): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/sessions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteSession: (id: number): Promise<ApiResponse<void>> =>
      apiRequest(`/admin/playground/sessions/${id}`, { method: 'DELETE' }),

    // Messages
    sendMessage: (sessionId: number, data: any): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    editMessage: (
      sessionId: number,
      msgId: number,
      data: any
    ): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/sessions/${sessionId}/messages/${msgId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteMessage: (
      sessionId: number,
      msgId: number
    ): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/sessions/${sessionId}/messages/${msgId}`, {
        method: 'DELETE',
      }),
    rerunFromMessage: (
      sessionId: number,
      msgId: number,
      data: any
    ): Promise<ApiResponse<any>> =>
      apiRequest(
        `/admin/playground/sessions/${sessionId}/messages/${msgId}/rerun`,
        { method: 'POST', body: JSON.stringify(data) }
      ),

    // Session actions
    exportSession: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/sessions/${id}/export`, { method: 'POST' }),
    saveSessionAsTestCase: (id: number, data: any): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/sessions/${id}/save-test-case`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Compare
    compare: (data: any): Promise<ApiResponse<any>> =>
      apiRequest('/admin/playground/compare', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Test Cases
    listTestCases: (opts?: {
      tags?: string
      limit?: number
      offset?: number
    }): Promise<ApiResponse<any[]>> => {
      const params = new URLSearchParams()
      if (opts?.limit) params.set('limit', String(opts.limit))
      if (opts?.offset) params.set('offset', String(opts.offset))
      if (opts?.tags) params.set('tags', opts.tags)
      return apiRequest(`/admin/playground/test-cases?${params.toString()}`)
    },
    getTestCase: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/test-cases/${id}`),
    createTestCase: (data: any): Promise<ApiResponse<any>> =>
      apiRequest('/admin/playground/test-cases', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateTestCase: (id: number, data: any): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/test-cases/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteTestCase: (id: number): Promise<ApiResponse<void>> =>
      apiRequest(`/admin/playground/test-cases/${id}`, { method: 'DELETE' }),
    importTestCases: (data: any): Promise<ApiResponse<any>> =>
      apiRequest('/admin/playground/test-cases/import', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    exportTestCase: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/test-cases/${id}/export`, {
        method: 'POST',
      }),
    runTestCase: (id: number, config: any): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/test-cases/${id}/run`, {
        method: 'POST',
        body: JSON.stringify(config),
      }),

    // Evaluations
    listEvaluations: (opts?: {
      limit?: number
      offset?: number
    }): Promise<ApiResponse<any[]>> =>
      apiRequest(
        `/admin/playground/evaluations?limit=${opts?.limit ?? 50}&offset=${opts?.offset ?? 0}`
      ),
    getEvaluation: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/evaluations/${id}`),
    createEvaluation: (data: any): Promise<ApiResponse<any>> =>
      apiRequest('/admin/playground/evaluations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    deleteEvaluation: (id: number): Promise<ApiResponse<void>> =>
      apiRequest(`/admin/playground/evaluations/${id}`, { method: 'DELETE' }),
    rerunEvaluation: (id: number): Promise<ApiResponse<any>> =>
      apiRequest(`/admin/playground/evaluations/${id}/rerun`, {
        method: 'POST',
      }),
  },

  // File upload APIs
  upload: uploadAPI,
  utils: apiUtils,
  keys: queryKeys,
}
